import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PlatformAdminSidebar } from '@/components/AdminSidebar';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import AnalyticsWidget from '@/components/AnalyticsWidget';
import ZippieSettingsCard from '@/components/ZippieSettingsCard';
import RichTextEditor from '@/components/RichTextEditor';
import PasswordStrengthMeter, { isPasswordValid } from '@/components/PasswordStrengthMeter';
import {
  Users, Building2, DollarSign, BookOpen, Trophy, Download, Upload,
  Search, Loader2, RefreshCw, Plus, Trash2, Edit2, Save, X, Eye, Key, 
  UserPlus, School, Church, GraduationCap, MessageSquare, HelpCircle, 
  AlertCircle, CheckCircle, Send, FileText, Archive, ArchiveRestore,
  BarChart3, TrendingUp, Calendar, Clock, UsersRound, ClipboardCheck, Mail,
  CheckSquare, XSquare, UserCheck, UserCog, Briefcase, ChevronDown, ChevronRight, User, Cross,
  Crown, Sliders, UserX, Activity, Receipt, Tag, Megaphone, Percent, Copy, Sparkles, AlertTriangle,
  Gift, Settings, XCircle, Bell, QrCode, Printer, Globe, Wallet, Banknote, Menu
} from 'lucide-react';
import { Image as ImageIcon } from 'lucide-react';
import { NewsletterSection } from './admin/sections/NewsletterSection';
import { RevenueSection } from './admin/sections/RevenueSection';
import { AnnouncementsSection } from './admin/sections/AnnouncementsSection';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import CRMSection from '@/components/CRMSection';
import ChristianAdminSection from '@/components/ChristianAdminSection';
import PlatformAdminBilling from '@/components/PlatformAdminBilling';
import PlatformAdminThemes from '@/components/PlatformAdminThemes';
import ClearCacheButton from '@/components/ClearCacheButton';

// ==================== FLYER GENERATOR SECTION ====================
const FlyerGeneratorSection = ({ api }) => {
  const [language, setLanguage] = useState('en');
  const [promoCode, setPromoCode] = useState('COPUSA');
  const [churchName, setChurchName] = useState('');
  const [contactEmail, setContactEmail] = useState('support@zazued.com');
  const [customTagline, setCustomTagline] = useState('');
  const [includeQr, setIncludeQr] = useState(true);
  const [qrCampaignId, setQrCampaignId] = useState('');
  const [templates, setTemplates] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [title, setTitle] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1e1b4b');
  const [accentColor, setAccentColor] = useState('#7c3aed');
  const [qrUrl, setQrUrl] = useState('');
  const [qrMode, setQrMode] = useState('url');
  const [iconSet, setIconSet] = useState('cross');
  const [layout, setLayout] = useState('poster');
  const [orientation, setOrientation] = useState('portrait');
  const [pageFormat, setPageFormat] = useState('letter');
  const [trialText, setTrialText] = useState('');
  const [pricingSub, setPricingSub] = useState('');
  const [customFeatures, setCustomFeatures] = useState(['', '', '', '']);
  const [customFeatureDescs, setCustomFeatureDescs] = useState(['', '', '', '']);
  const [ctaText, setCtaText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialTwitter, setSocialTwitter] = useState('');
  const [socialYoutube, setSocialYoutube] = useState('');
  const [socialWebsite, setSocialWebsite] = useState('');

  // Template Gallery State
  const [gallery, setGallery] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [loadingGallery, setLoadingGallery] = useState(false);

  useEffect(() => {
    api.get('/admin/flyers/templates').then(r => setTemplates(r.data)).catch(console.error);
  }, []);

  const fetchGallery = async () => {
    try {
      setLoadingGallery(true);
      const r = await api.get('/admin/flyers/gallery');
      setGallery(r.data.templates || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingGallery(false);
    }
  };

  useEffect(() => { fetchGallery(); }, []);

  const getCurrentConfig = () => ({
    language, promo_code: promoCode, church_name: churchName,
    contact_email: contactEmail, custom_tagline: customTagline,
    include_qr: includeQr, qr_campaign_id: qrMode === 'campaign' ? qrCampaignId : '',
    title, primary_color: primaryColor, accent_color: accentColor,
    qr_url: qrMode === 'url' ? qrUrl : '', icon_set: iconSet, layout,
    orientation, page_format: pageFormat,
    trial_text: trialText, pricing_sub_text: pricingSub,
    custom_features: customFeatures, custom_feature_descs: customFeatureDescs,
    cta_text: ctaText, footer_text: footerText, phone_number: phoneNumber,
    social_facebook: socialFacebook, social_instagram: socialInstagram,
    social_twitter: socialTwitter, social_youtube: socialYoutube, social_website: socialWebsite
  });

  const loadTemplate = (tpl) => {
    const c = tpl.config;
    setLanguage(c.language || 'en');
    setPromoCode(c.promo_code || 'COPUSA');
    setChurchName(c.church_name || '');
    setContactEmail(c.contact_email || 'support@zazued.com');
    setCustomTagline(c.custom_tagline || '');
    setIncludeQr(c.include_qr !== false);
    setQrCampaignId(c.qr_campaign_id || '');
    setTitle(c.title || '');
    setPrimaryColor(c.primary_color || '#1e1b4b');
    setAccentColor(c.accent_color || '#7c3aed');
    setQrUrl(c.qr_url || '');
    setQrMode(c.qr_url ? 'url' : c.qr_campaign_id ? 'campaign' : 'url');
    setIconSet(c.icon_set || 'cross');
    setLayout(c.layout || 'poster');
    setOrientation(c.orientation || 'portrait');
    setPageFormat(c.page_format || 'letter');
    setTrialText(c.trial_text || '');
    setPricingSub(c.pricing_sub_text || '');
    setCustomFeatures(c.custom_features?.length === 4 ? c.custom_features : ['', '', '', '']);
    setCustomFeatureDescs(c.custom_feature_descs?.length === 4 ? c.custom_feature_descs : ['', '', '', '']);
    setCtaText(c.cta_text || '');
    setFooterText(c.footer_text || '');
    setPhoneNumber(c.phone_number || '');
    setSocialFacebook(c.social_facebook || '');
    setSocialInstagram(c.social_instagram || '');
    setSocialTwitter(c.social_twitter || '');
    setSocialYoutube(c.social_youtube || '');
    setSocialWebsite(c.social_website || '');
    setShowGallery(false);
    toast.success(`Loaded template: ${tpl.name}`);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) { toast.error('Template name is required'); return; }
    try {
      setSavingTemplate(true);
      await api.post('/admin/flyers/gallery', {
        name: templateName.trim(),
        description: templateDesc.trim(),
        config: getCurrentConfig()
      });
      toast.success('Template saved!');
      setShowSaveDialog(false);
      setTemplateName('');
      setTemplateDesc('');
      fetchGallery();
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const updateTemplate = async (tplId) => {
    try {
      await api.put(`/admin/flyers/gallery/${tplId}`, { config: getCurrentConfig() });
      toast.success('Template updated with current settings');
      fetchGallery();
    } catch (err) {
      toast.error('Failed to update template');
    }
  };

  const deleteTemplate = async (tplId) => {
    try {
      await api.delete(`/admin/flyers/gallery/${tplId}`);
      toast.success('Template deleted');
      fetchGallery();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const generateFlyer = async () => {
    try {
      setGenerating(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/flyers/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('littlelearners-token')}`
        },
        body: JSON.stringify({
          language, promo_code: promoCode, church_name: churchName,
          contact_email: contactEmail, custom_tagline: customTagline,
          include_qr: includeQr,
          qr_campaign_id: qrMode === 'campaign' ? qrCampaignId : '',
          title, primary_color: primaryColor, accent_color: accentColor,
          qr_url: qrMode === 'url' ? qrUrl : '',
          icon_set: iconSet,
          layout,
          trial_text: trialText,
          pricing_sub_text: pricingSub,
          custom_features: customFeatures.filter(f => f.trim()),
          custom_feature_descs: customFeatureDescs.filter(f => f.trim()),
          cta_text: ctaText,
          footer_text: footerText,
          phone_number: phoneNumber,
          social_facebook: socialFacebook,
          social_instagram: socialInstagram,
          social_twitter: socialTwitter,
          social_youtube: socialYoutube,
          social_website: socialWebsite
        })
      });
      if (!response.ok) throw new Error('Failed to generate flyer');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zazu_flyer_${language}_${promoCode.toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Flyer downloaded!');
    } catch (err) {
      toast.error('Failed to generate flyer: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const applyPreset = (preset) => {
    setPrimaryColor(preset.primary);
    setAccentColor(preset.accent);
  };

  const defaultTitles = { en: 'ZAZU CHRISTIAN EDUCATION', es: 'ZAZU EDUCACION CRISTIANA', fr: 'ZAZU EDUCATION CHRETIENNE' };
  const displayTitle = title || defaultTitles[language] || defaultTitles.en;

  const taglineDefaults = {
    en: "Nurture Your Child's Faith",
    es: "Nutra la Fe de Su Hijo",
    fr: "Nourrissez la Foi de Votre Enfant"
  };
  const displayTagline = customTagline || taglineDefaults[language] || taglineDefaults.en;

  const featureLabels = {
    cross: ['Faith', 'Love', 'Hope', 'Praise'],
    education: ['Learn', 'Excel', 'Grow', 'Joy'],
    nature: ['Light', 'Heart', 'Life', 'Shine'],
    minimal: ['Stories', 'Verses', 'Songs', 'Badges'],
  };

  const displayTrialText = trialText || '7-Day Free Trial | $2.99/month';
  const displayFeatures = customFeatures.some(f => f.trim())
    ? customFeatures.map((f, i) => f || (featureLabels[iconSet] || featureLabels.cross)[i])
    : (featureLabels[iconSet] || featureLabels.cross);
  const displayCta = ctaText || 'Join thousands of families growing in faith!';
  const displayFooter = footerText || 'Faith-Based Learning for the Whole Family';

  const iconSymbols = {
    cross: ['\u2020', '\u2665', '\u2606', '\u266A'],
    education: ['\u270E', '\u2605', '\u2602', '\u263A'],
    nature: ['\u2600', '\u2764', '\u2618', '\u2728'],
    minimal: ['\u25CF', '\u25CF', '\u25CF', '\u25CF'],
  };

  return (
    <div className="space-y-6" data-testid="flyer-generator-section">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Printer className="w-7 h-7 text-purple-500" /> Flyer Generator
          </h1>
          <p className="text-slate-500 text-sm mt-1">Create customized printable flyers for churches and organizations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setShowGallery(!showGallery); if (!showGallery) fetchGallery(); }}
            data-testid="flyer-gallery-toggle" className="gap-1.5">
            <Archive className="w-4 h-4" />
            {showGallery ? 'Hide Gallery' : 'My Templates'}
            {gallery.length > 0 && <span className="ml-1 bg-purple-100 text-purple-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{gallery.length}</span>}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSaveDialog(true)} data-testid="flyer-save-template-btn" className="gap-1.5">
            <Save className="w-4 h-4" /> Save as Template
          </Button>
        </div>
      </div>

      {/* Template Gallery */}
      {showGallery && (
        <Card className="border-purple-200 bg-purple-50/30" data-testid="flyer-template-gallery">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Archive className="w-4 h-4 text-purple-500" /> Saved Templates
            </CardTitle>
            <CardDescription>Click a template to load its configuration into the editor</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingGallery ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-purple-500" /></div>
            ) : gallery.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Archive className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium">No saved templates yet</p>
                <p className="text-xs mt-1">Configure your flyer and click "Save as Template" to create one</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {gallery.map(tpl => (
                  <div key={tpl.id} className="group bg-white rounded-lg border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all p-3 cursor-pointer relative"
                    data-testid={`gallery-template-${tpl.id}`}>
                    <div onClick={() => loadTemplate(tpl)} className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-slate-800 truncate">{tpl.name}</h4>
                          {tpl.description && <p className="text-[11px] text-slate-400 line-clamp-1 mt-0.5">{tpl.description}</p>}
                        </div>
                        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full ml-2 flex-shrink-0"
                          style={{
                            backgroundColor: tpl.config?.layout === 'magazine' ? '#dbeafe' : tpl.config?.layout === 'minimal' ? '#dcfce7' : '#f3e8ff',
                            color: tpl.config?.layout === 'magazine' ? '#1d4ed8' : tpl.config?.layout === 'minimal' ? '#15803d' : '#7c3aed'
                          }}>
                          {tpl.config?.layout || 'poster'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <span className="w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: tpl.config?.primary_color || '#1e1b4b' }} />
                          <span className="w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: tpl.config?.accent_color || '#7c3aed' }} />
                        </div>
                        {tpl.config?.church_name && (
                          <span className="text-[10px] text-slate-400 truncate">{tpl.config.church_name}</span>
                        )}
                        {tpl.config?.promo_code && (
                          <span className="text-[10px] font-mono bg-slate-100 px-1 rounded text-slate-500">{tpl.config.promo_code}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-slate-400">
                        <span>{tpl.created_by}</span>
                        <span>{new Date(tpl.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); updateTemplate(tpl.id); }}
                        className="w-6 h-6 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center transition-colors"
                        title="Overwrite with current settings" data-testid={`gallery-update-${tpl.id}`}>
                        <RefreshCw className="w-3 h-3" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${tpl.name}"?`)) deleteTemplate(tpl.id); }}
                        className="w-6 h-6 rounded-md bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center transition-colors"
                        title="Delete template" data-testid={`gallery-delete-${tpl.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save Template Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Save className="w-5 h-5 text-purple-500" /> Save as Template</DialogTitle>
            <DialogDescription>Save your current flyer configuration for easy reuse</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Template Name *</Label>
              <Input value={templateName} onChange={e => setTemplateName(e.target.value)}
                placeholder="e.g., Grace Church Easter Flyer" className="mt-1" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={templateDesc} onChange={e => setTemplateDesc(e.target.value)}
                placeholder="Brief description of this template" className="mt-1" data-testid="template-desc-input" />
            </div>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
              <p className="text-xs font-semibold text-slate-600">Configuration Summary</p>
              <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
                <span className="bg-white px-2 py-0.5 rounded border">Layout: {layout}</span>
                <span className="bg-white px-2 py-0.5 rounded border">Lang: {language}</span>
                {churchName && <span className="bg-white px-2 py-0.5 rounded border">{churchName}</span>}
                <span className="bg-white px-2 py-0.5 rounded border">Code: {promoCode}</span>
                <span className="flex items-center gap-1 bg-white px-2 py-0.5 rounded border">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />
                  Colors
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={savingTemplate || !templateName.trim()} data-testid="template-save-confirm-btn"
              style={{ backgroundColor: '#7c3aed' }} className="text-white">
              {savingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <div className="space-y-4">
          {/* Content Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Flyer Title</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={defaultTitles[language]} className="mt-1" data-testid="flyer-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="mt-1" data-testid="flyer-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(templates?.languages || []).map(l => (
                        <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Promo Code</Label>
                  <Input value={promoCode} onChange={e => setPromoCode(e.target.value)} className="mt-1" data-testid="flyer-promo-input" />
                </div>
              </div>
              <div>
                <Label>Church / Organization Name</Label>
                <Input value={churchName} onChange={e => setChurchName(e.target.value)} placeholder="e.g., Grace Community Church" className="mt-1" data-testid="flyer-church-input" />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="mt-1" data-testid="flyer-email-input" />
              </div>
              <div>
                <Label>Custom Tagline (optional)</Label>
                <Input value={customTagline} onChange={e => setCustomTagline(e.target.value)} placeholder="Leave empty for default" className="mt-1" data-testid="flyer-tagline-input" />
              </div>
            </CardContent>
          </Card>

          {/* Pricing & Trial */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><DollarSign className="w-4 h-4" /> Pricing & Trial</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Trial / Pricing Text</Label>
                <Input value={trialText} onChange={e => setTrialText(e.target.value)} placeholder="7-Day Free Trial  |  Then Only $2.99/month" className="mt-1" data-testid="flyer-trial-text" />
              </div>
              <div>
                <Label className="text-xs">Pricing Sub-text</Label>
                <Input value={pricingSub} onChange={e => setPricingSub(e.target.value)} placeholder="Cancel anytime - No commitment required" className="mt-1" data-testid="flyer-pricing-sub" />
              </div>
            </CardContent>
          </Card>

          {/* Editable Features */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Feature Highlights</CardTitle>
              <CardDescription className="text-[11px]">Leave empty to use defaults for selected language</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="flex gap-2">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white mt-1" style={{ backgroundColor: accentColor }}>{i + 1}</div>
                  <div className="flex-1 space-y-1">
                    <Input value={customFeatures[i]} onChange={e => { const n = [...customFeatures]; n[i] = e.target.value; setCustomFeatures(n); }}
                      placeholder={['Bible Stories & Adventures', 'Memory Verses & Activities', 'Worship Songs & Music', 'Badges, Rewards & Progress'][i]}
                      className="h-8 text-xs" data-testid={`flyer-feature-${i}`} />
                    <Input value={customFeatureDescs[i]} onChange={e => { const n = [...customFeatureDescs]; n[i] = e.target.value; setCustomFeatureDescs(n); }}
                      placeholder="Short description..." className="h-7 text-[11px] text-slate-500" data-testid={`flyer-feature-desc-${i}`} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* CTA & Footer */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Megaphone className="w-4 h-4" /> CTA & Footer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Call-to-Action Text</Label>
                <Input value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="Join thousands of families growing in faith together!" className="mt-1" data-testid="flyer-cta-text" />
              </div>
              <div>
                <Label className="text-xs">Footer Text</Label>
                <Input value={footerText} onChange={e => setFooterText(e.target.value)} placeholder="Faith-Based Learning for the Whole Family" className="mt-1" data-testid="flyer-footer-text" />
              </div>
            </CardContent>
          </Card>

          {/* Contact & Social */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Globe className="w-4 h-4" /> Contact & Social Media</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Phone Number</Label>
                <Input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="(555) 123-4567" className="mt-1" data-testid="flyer-phone" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-400">Facebook</Label>
                  <Input value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} placeholder="@churchname" className="mt-0.5 h-8 text-xs" data-testid="flyer-social-fb" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400">Instagram</Label>
                  <Input value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} placeholder="@churchname" className="mt-0.5 h-8 text-xs" data-testid="flyer-social-ig" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400">X (Twitter)</Label>
                  <Input value={socialTwitter} onChange={e => setSocialTwitter(e.target.value)} placeholder="@churchname" className="mt-0.5 h-8 text-xs" data-testid="flyer-social-tw" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400">YouTube</Label>
                  <Input value={socialYoutube} onChange={e => setSocialYoutube(e.target.value)} placeholder="youtube.com/c/..." className="mt-0.5 h-8 text-xs" data-testid="flyer-social-yt" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-slate-400">Website</Label>
                <Input value={socialWebsite} onChange={e => setSocialWebsite(e.target.value)} placeholder="www.yourchurch.com" className="mt-0.5 h-8 text-xs" data-testid="flyer-social-web" />
              </div>
            </CardContent>
          </Card>

          {/* Color & Style Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4" /> Colors & Style</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Color Presets</Label>
                <div className="flex flex-wrap gap-2">
                  {(templates?.color_presets || [
                    { name: 'Royal Purple', primary: '#1e1b4b', accent: '#7c3aed' },
                    { name: 'Deep Blue', primary: '#0f172a', accent: '#3b82f6' },
                    { name: 'Forest Green', primary: '#14332a', accent: '#10b981' },
                    { name: 'Burgundy', primary: '#3b0a0a', accent: '#dc2626' },
                    { name: 'Midnight Gold', primary: '#1a1a2e', accent: '#f59e0b' },
                    { name: 'Ocean Teal', primary: '#042f2e', accent: '#14b8a6' },
                  ]).map(p => (
                    <button key={p.name} onClick={() => applyPreset(p)}
                      className={cn("flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-all",
                        primaryColor === p.primary && accentColor === p.accent
                          ? "border-purple-500 bg-purple-50 ring-1 ring-purple-300"
                          : "border-slate-200 hover:border-slate-400"
                      )} data-testid={`flyer-preset-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                      <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: p.primary }} />
                      <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: p.accent }} />
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Primary Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                      className="w-9 h-9 rounded border cursor-pointer" data-testid="flyer-primary-color" />
                    <Input value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="flex-1 text-xs font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Accent Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                      className="w-9 h-9 rounded border cursor-pointer" data-testid="flyer-accent-color" />
                    <Input value={accentColor} onChange={e => setAccentColor(e.target.value)} className="flex-1 text-xs font-mono" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Icon Set</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {(templates?.icon_sets || [
                    { id: 'cross', name: 'Faith & Worship' },
                    { id: 'education', name: 'Education' },
                    { id: 'nature', name: 'Nature & Light' },
                    { id: 'minimal', name: 'Minimal' },
                  ]).map(s => (
                    <button key={s.id} onClick={() => setIconSet(s.id)}
                      className={cn("px-3 py-2 rounded-md border text-xs font-medium transition-all text-left",
                        iconSet === s.id
                          ? "border-purple-500 bg-purple-50 ring-1 ring-purple-300"
                          : "border-slate-200 hover:border-slate-400"
                      )} data-testid={`flyer-icon-${s.id}`}>
                      <span className="text-base mr-1.5">{(iconSymbols[s.id] || []).join(' ')}</span>
                      <span className="block mt-0.5">{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Layout Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Layout Style</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: 'banner', name: 'Banner', desc: 'Teal header, subject bubbles', icon: '🌊' },
                  { id: 'device', name: 'Device', desc: 'Phone mockup + checklist', icon: '📱' },
                  { id: 'notebook', name: 'Notebook', desc: 'Handwritten paper feel', icon: '📓' },
                  { id: 'hero', name: 'Dark Hero', desc: 'Bold ZAZU + stats', icon: '⭐' },
                  { id: 'split', name: 'Split Promo', desc: 'Sidebar + promo code', icon: '↔️' },
                  { id: 'poster', name: 'Poster', desc: 'Bold hero, full-width', icon: '🖼' },
                  { id: 'magazine', name: 'Magazine', desc: 'Split, images right', icon: '📰' },
                  { id: 'minimal', name: 'Minimal', desc: 'Clean, centered', icon: '✨' },
                ].map(l => (
                  <button key={l.id} onClick={() => setLayout(l.id)}
                    className={cn("p-3 rounded-lg border text-left transition-all",
                      layout === l.id
                        ? "border-purple-500 bg-purple-50 ring-1 ring-purple-300"
                        : "border-slate-200 hover:border-slate-400"
                    )} data-testid={`flyer-layout-${l.id}`}>
                    <span className="text-lg block mb-1">{l.icon}</span>
                    <span className="text-xs font-semibold block">{l.name}</span>
                    <span className="text-[10px] text-slate-400 leading-tight block">{l.desc}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Orientation + Page Format */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Format & Orientation</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Orientation</Label>
                <div className="flex gap-2">
                  {[{ id: 'portrait', label: 'Portrait' }, { id: 'landscape', label: 'Landscape' }].map((o) => (
                    <button
                      key={o.id}
                      onClick={() => setOrientation(o.id)}
                      className={cn("flex-1 py-2 rounded-md border text-xs font-semibold transition-all",
                        orientation === o.id ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-400"
                      )}
                      data-testid={`flyer-orientation-${o.id}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Page format</Label>
                <select
                  value={pageFormat}
                  onChange={(e) => setPageFormat(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-2 py-2 text-xs"
                  data-testid="flyer-format-select"
                >
                  <option value="letter">Letter (US · 8.5×11)</option>
                  <option value="a4">A4 (metric · 210×297mm)</option>
                  <option value="a5">A5 (metric · 148×210mm)</option>
                  <option value="square">Square 1:1 (Instagram)</option>
                  <option value="ig_story">9:16 Story (Instagram/TikTok)</option>
                  <option value="landscape_letter">Landscape Letter (handout)</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* QR Code Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><QrCode className="w-4 h-4" /> QR Code</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch checked={includeQr} onCheckedChange={setIncludeQr} data-testid="flyer-qr-toggle" />
                <Label>Include QR Code on Flyer</Label>
              </div>
              {includeQr && (
                <>
                  <div className="flex gap-2">
                    <button onClick={() => setQrMode('url')}
                      className={cn("flex-1 py-1.5 rounded-md border text-xs font-medium transition-all",
                        qrMode === 'url' ? "border-purple-500 bg-purple-50" : "border-slate-200"
                      )} data-testid="flyer-qr-mode-url">
                      Custom URL
                    </button>
                    <button onClick={() => setQrMode('campaign')}
                      className={cn("flex-1 py-1.5 rounded-md border text-xs font-medium transition-all",
                        qrMode === 'campaign' ? "border-purple-500 bg-purple-50" : "border-slate-200"
                      )} data-testid="flyer-qr-mode-campaign">
                      From Campaign
                    </button>
                  </div>
                  {qrMode === 'url' ? (
                    <div>
                      <Label className="text-xs">QR Code URL</Label>
                      <Input value={qrUrl} onChange={e => setQrUrl(e.target.value)}
                        placeholder="https://zazued.com/christian" className="mt-1 text-sm"
                        data-testid="flyer-qr-url-input" />
                      <p className="text-[10px] text-slate-400 mt-1">A QR code will be generated from this URL</p>
                    </div>
                  ) : (
                    <Select value={qrCampaignId} onValueChange={setQrCampaignId}>
                      <SelectTrigger data-testid="flyer-qr-campaign-select"><SelectValue placeholder="Select QR Campaign" /></SelectTrigger>
                      <SelectContent>
                        {(templates?.qr_campaigns || []).map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Generate Button */}
          <Button onClick={generateFlyer} disabled={generating} className="w-full h-12 text-base font-semibold" style={{ backgroundColor: accentColor }}
            data-testid="flyer-generate-btn">
            {generating ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
            {generating ? 'Generating PDF...' : 'Generate & Download PDF'}
          </Button>
        </div>

        {/* Live Preview */}
        <div className="lg:sticky lg:top-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Live Preview</CardTitle>
              <CardDescription>8.5" x 11" — {layout === 'poster' ? 'Poster' : layout === 'magazine' ? 'Magazine' : 'Minimal'} Layout</CardDescription>
            </CardHeader>
            <CardContent>
              {layout === 'magazine' ? (
                /* Magazine Layout Preview */
                <div className="aspect-[8.5/11] rounded-lg relative overflow-hidden shadow-xl flex">
                  {/* Left column - white */}
                  <div className="w-[58%] bg-white p-4 flex flex-col justify-between relative">
                    <div className="h-1 w-full absolute top-0 left-0" style={{ backgroundColor: accentColor }} />
                    <div className="pt-2">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 rounded-full bg-emerald-400" />
                        <div>
                          <h3 className="text-[9px] font-bold" style={{ color: primaryColor }}>{displayTitle}</h3>
                          {churchName && <p className="text-[6px] italic" style={{ color: accentColor }}>{churchName}</p>}
                        </div>
                      </div>
                      <div className="w-full h-[1px] my-1.5" style={{ backgroundColor: accentColor }} />
                      <p className="text-[8px] font-bold" style={{ color: primaryColor }}>{displayTagline}</p>
                      <p className="text-[6px] text-slate-400 mt-0.5">52-Week Bible Curriculum</p>
                    </div>
                    <div className="space-y-1.5 my-2">
                      {displayFeatures.map((label, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <span className="w-3 h-3 rounded-full flex items-center justify-center text-[5px] text-white flex-shrink-0 mt-0.5" style={{ backgroundColor: accentColor }}>{i+1}</span>
                          <span className="text-[6px] font-medium" style={{ color: primaryColor }}>{label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="rounded-sm py-1.5 px-2" style={{ backgroundColor: accentColor }}>
                      <p className="text-[5px] text-white/70">DISCOUNT CODE</p>
                      <p className="text-[10px] font-bold text-white tracking-wider">{promoCode || 'COPUSA'}</p>
                      <p className="text-[5px] text-emerald-300 font-bold">{displayTrialText}</p>
                    </div>
                    <div className="mt-1">
                      <p className="text-[7px] font-bold" style={{ color: accentColor }}>zazued.com/christian</p>
                      <p className="text-[5px] text-slate-400">{contactEmail}{phoneNumber ? ` | ${phoneNumber}` : ''}</p>
                    </div>
                  </div>
                  {/* Right column - dark */}
                  <div className="w-[42%] flex flex-col justify-between p-2" style={{ backgroundColor: primaryColor }}>
                    <div className="rounded-sm overflow-hidden h-[30%] bg-slate-700 flex items-center justify-center">
                      <span className="text-[7px] text-slate-400">Children Learning</span>
                    </div>
                    <div className="rounded-sm overflow-hidden h-[25%] bg-slate-700 flex items-center justify-center">
                      <span className="text-[7px] text-slate-400">Happy Family</span>
                    </div>
                    <div className="rounded-sm overflow-hidden h-[10%] bg-slate-700 flex items-center justify-center">
                      <span className="text-[5px] text-slate-400">Faith Icons</span>
                    </div>
                    <p className="text-[5px] text-center italic" style={{ color: `${accentColor}aa` }}>Join thousands of families!</p>
                    {includeQr && (qrUrl || qrCampaignId) && (
                      <div className="flex justify-center">
                        <div className="w-7 h-7 bg-white rounded flex items-center justify-center">
                          <QrCode className="w-5 h-5 text-slate-800" />
                        </div>
                      </div>
                    )}
                    <p className="text-[5px] text-center text-white/60">Trusted by churches worldwide</p>
                    <div className="h-1 w-full absolute bottom-0 left-0" style={{ backgroundColor: accentColor }} />
                  </div>
                </div>
              ) : layout === 'minimal' ? (
                /* Minimal Layout Preview */
                <div className="aspect-[8.5/11] rounded-lg bg-white text-center relative overflow-hidden shadow-xl">
                  <div className="h-1 w-full" style={{ backgroundColor: accentColor }} />
                  <div className="flex flex-col h-full justify-between p-5 pt-3">
                    <div>
                      <div className="w-6 h-6 rounded-full mx-auto bg-emerald-400 mb-1" />
                      <h3 className="text-[10px] font-bold" style={{ color: primaryColor }}>{displayTitle}</h3>
                      {churchName && <p className="text-[7px] italic" style={{ color: accentColor }}>{churchName}</p>}
                      <div className="w-12 h-[1px] mx-auto my-2" style={{ backgroundColor: accentColor }} />
                      <p className="text-[8px]" style={{ color: primaryColor }}>{displayTagline}</p>
                      <p className="text-[6px] text-slate-400">52-Week Bible Curriculum</p>
                    </div>
                    <div className="w-full h-[22%] rounded bg-slate-100 flex items-center justify-center">
                      <span className="text-[7px] text-slate-400">Children Bible Learning Image</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-left">
                      {displayFeatures.map((label, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full text-[4px] text-white flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accentColor }}>{i+1}</span>
                          <span className="text-[6px]" style={{ color: primaryColor }}>{label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="w-full h-[6%] rounded bg-slate-800 flex items-center justify-center">
                      <span className="text-[5px] text-slate-400">Faith Icons</span>
                    </div>
                    <div className="rounded-sm py-1.5 px-3" style={{ backgroundColor: primaryColor }}>
                      <p className="text-[5px] text-slate-300">DISCOUNT CODE</p>
                      <p className="text-[10px] font-bold text-white tracking-wider">{promoCode || 'COPUSA'}</p>
                      <p className="text-[5px] text-emerald-300 font-bold">{displayTrialText}</p>
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      {includeQr && (qrUrl || qrCampaignId) && (
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center border">
                          <QrCode className="w-4 h-4 text-slate-700" />
                        </div>
                      )}
                      <div>
                        <p className="text-[7px] font-bold" style={{ color: accentColor }}>zazued.com/christian</p>
                        <p className="text-[5px] text-slate-400">{contactEmail}{phoneNumber ? ` | ${phoneNumber}` : ''}</p>
                      </div>
                    </div>
                  </div>
                  <div className="h-1 w-full absolute bottom-0" style={{ backgroundColor: accentColor }} />
                </div>
              ) : (
                /* Poster Layout Preview (default) */
                <div className="aspect-[8.5/11] rounded-lg text-white text-center relative overflow-hidden shadow-xl"
                  style={{ background: `linear-gradient(to bottom, ${primaryColor}, ${primaryColor}dd, ${primaryColor}bb)` }}>
                  <div className="h-1.5 w-full" style={{ backgroundColor: accentColor }} />
                  {/* Hero image placeholder */}
                  <div className="w-full h-[30%] bg-gradient-to-b from-slate-700 via-slate-600 to-transparent flex items-center justify-center relative">
                    <span className="text-[8px] text-slate-300 z-10">Children Bible Learning Photo</span>
                    <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white/90 flex items-center justify-center">
                      <span className="text-[6px] text-emerald-600 font-bold">Z</span>
                    </div>
                  </div>
                  <div className="relative z-10 flex flex-col justify-between px-4 pb-3" style={{ height: '68%' }}>
                    <div>
                      <h3 className="text-[11px] font-bold tracking-[0.12em]">{displayTitle}</h3>
                      {churchName && <p className="text-[7px] italic" style={{ color: accentColor }}>Presented by {churchName}</p>}
                      <p className="text-[8px] mt-1 font-semibold" style={{ color: `${accentColor}cc` }}>{displayTagline}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {displayFeatures.map((label, i) => (
                        <div key={i} className="rounded-sm px-1 py-0.5 text-left flex items-center gap-1" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                          <span className="w-3 h-3 rounded-full flex items-center justify-center text-[5px] text-white flex-shrink-0" style={{ backgroundColor: accentColor }}>{i+1}</span>
                          <span className="text-[6px] font-medium">{label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="w-full h-[8%] rounded bg-slate-700/50 flex items-center justify-center">
                      <span className="text-[5px] text-slate-300">Faith Icons Banner</span>
                    </div>
                    <div className="rounded-md py-1.5 px-2 border border-white/20" style={{ backgroundColor: accentColor }}>
                      <p className="text-[5px] text-white/70">SPECIAL DISCOUNT</p>
                      <p className="text-sm font-bold tracking-widest">{promoCode || 'COPUSA'}</p>
                      <p className="text-[6px] text-emerald-300 font-semibold">{displayTrialText}</p>
                    </div>
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-[30%] h-[12%] rounded bg-slate-700/50 flex items-center justify-center">
                        <span className="text-[5px] text-slate-300">Family Photo</span>
                      </div>
                      <div className="text-left">
                        <p className="text-[7px] font-bold" style={{ color: `${accentColor}dd` }}>zazued.com/christian</p>
                        {includeQr && (qrUrl || qrCampaignId) && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <div className="w-4 h-4 bg-white rounded flex items-center justify-center">
                              <QrCode className="w-3 h-3 text-slate-800" />
                            </div>
                            <span className="text-[5px] text-slate-400">Scan to start</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-[5px] text-slate-400">{contactEmail}{phoneNumber ? ` | ${phoneNumber}` : ''}</p>
                  </div>
                  <div className="h-1.5 w-full absolute bottom-0" style={{ backgroundColor: accentColor }} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Available Promo Codes */}
      {templates?.promo_codes?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Available Promo Codes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {templates.promo_codes.map(p => (
                <Badge key={p.code} variant="outline" className="cursor-pointer hover:bg-purple-50 transition-colors"
                  onClick={() => setPromoCode(p.code)}>
                  <Tag className="w-3 h-3 mr-1" /> {p.code} ({p.discount_type === 'percentage' ? `${p.discount_value}%` : `$${p.discount_value}`})
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== KNOWLEDGE BASE ADMIN SECTION ====================
const KBAdminSection = ({ api }) => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', content: '', category: 'Getting Started', tags: '',
    audience: 'all', order: 0, is_published: true
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Maximum 5MB.');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/knowledge-base/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('littlelearners-token')}` },
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      
      // Insert markdown image at cursor position or end
      const imgMarkdown = `\n${data.markdown}\n`;
      setForm(prev => ({ ...prev, content: prev.content + imgMarkdown }));
      toast.success(`Image "${file.name}" uploaded and inserted`);
    } catch (err) {
      toast.error('Failed to upload image: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fetchArticles = async () => {
    try {
      const res = await api.get('/admin/knowledge-base/articles');
      setArticles(res.data.articles || []);
      setCategories(res.data.categories || []);
      setAllTags(res.data.tags || []);
    } catch (err) {
      console.error('KB admin fetch failed:', err?.response?.status, err?.response?.data);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('You need platform admin access to manage Knowledge Base articles.');
      } else {
        toast.error(`Failed to load articles (${status || 'network error'}). Try Refresh or Reseed.`);
      }
    }
  };

  const reseedArticles = async () => {
    if (!window.confirm('Reseed the default 22 Help Center articles? Existing articles with the same slug will be refreshed; new ones will be added. This is idempotent.')) return;
    try {
      const res = await api.post('/admin/knowledge-base/reseed');
      toast.success(`Reseed complete — ${res.data.total || 0} articles now available.`);
      fetchArticles();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'unknown error';
      toast.error(`Reseed failed: ${detail}`);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  const resetForm = () => {
    setForm({ title: '', content: '', category: 'Getting Started', tags: '', audience: 'all', order: 0, is_published: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        order: parseInt(form.order) || 0
      };
      if (editingId) {
        await api.put(`/admin/knowledge-base/articles/${editingId}`, payload);
        toast.success('Article updated');
      } else {
        await api.post('/admin/knowledge-base/articles', payload);
        toast.success('Article created');
      }
      resetForm();
      fetchArticles();
    } catch (err) {
      toast.error('Failed to save: ' + err.message);
    } finally { setSaving(false); }
  };

  const handleEdit = (article) => {
    setForm({
      title: article.title,
      content: article.content,
      category: article.category || 'Getting Started',
      tags: (article.tags || []).join(', '),
      audience: article.audience || 'all',
      order: article.order || 0,
      is_published: article.is_published !== false
    });
    setEditingId(article.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await api.delete(`/admin/knowledge-base/articles/${id}`);
      toast.success('Article deleted');
      fetchArticles();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleTogglePublish = async (article) => {
    try {
      await api.put(`/admin/knowledge-base/articles/${article.id}`, { is_published: !article.is_published });
      fetchArticles();
    } catch (err) { toast.error('Failed to update'); }
  };

  const categoryOptions = ['Getting Started', 'Organization Setup', 'Progress & Reports', 'Billing & Subscriptions', 'Curriculum', 'FAQ'];

  return (
    <div className="space-y-6" data-testid="kb-admin-section">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-emerald-500" /> Knowledge Base
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage articles for organization, school, and church admins</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchArticles} data-testid="kb-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
          <Button variant="outline" onClick={reseedArticles} data-testid="kb-reseed-btn" title="Restore the default 22 Help Center articles (idempotent)">
            <RefreshCw className="w-4 h-4 mr-2" /> Reseed defaults
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="kb-add-article-btn">
            <Plus className="w-4 h-4 mr-2" /> New Article
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{articles.length}</p>
          <p className="text-xs text-slate-500">Total Articles</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{categories.length}</p>
          <p className="text-xs text-slate-500">Categories</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{articles.filter(a => a.is_published).length}</p>
          <p className="text-xs text-slate-500">Published</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{articles.reduce((s, a) => s + (a.views || 0), 0)}</p>
          <p className="text-xs text-slate-500">Total Views</p>
        </CardContent></Card>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="border-2 border-emerald-200 bg-emerald-50/30">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{editingId ? 'Edit Article' : 'New Article'}</h3>
              <Button size="sm" variant="ghost" onClick={resetForm}><X className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1" data-testid="kb-form-title" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger className="mt-1" data-testid="kb-form-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Audience</Label>
                  <Select value={form.audience} onValueChange={v => setForm({...form, audience: v})}>
                    <SelectTrigger className="mt-1" data-testid="kb-form-audience">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="school">Schools</SelectItem>
                      <SelectItem value="church">Churches</SelectItem>
                      <SelectItem value="organization">Organizations</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Order</Label>
                  <Input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} className="mt-1" />
                </div>
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="students, upload, csv" className="mt-1" data-testid="kb-form-tags" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Content (Markdown supported)</Label>
                  <div className="flex items-center gap-2">
                    <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleImageUpload} data-testid="kb-image-file-input" />
                    <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="kb-upload-image-btn">
                      {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ImageIcon className="w-3 h-3 mr-1" />}
                      {uploading ? 'Uploading...' : 'Insert Image'}
                    </Button>
                  </div>
                </div>
                <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="mt-1 min-h-[250px] font-mono text-sm" data-testid="kb-form-content" />
                <p className="text-xs text-slate-400 mt-1">Use Markdown syntax. Images: ![alt text](url). Click "Insert Image" to upload and auto-insert.</p>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch checked={form.is_published} onCheckedChange={v => setForm({...form, is_published: v})} />
                  <Label>Published</Label>
                </div>
                <Button onClick={handleSave} disabled={saving || !form.title || !form.content} data-testid="kb-form-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingId ? 'Update Article' : 'Create Article'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Articles List */}
      <div className="space-y-3">
        {articles.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-slate-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>No knowledge base articles yet. Click "New Article" to create one.</p>
          </CardContent></Card>
        ) : (
          articles.map(article => (
            <Card key={article.id} className={cn("transition-all", !article.is_published && "opacity-60")}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-slate-800">{article.title}</h4>
                      {!article.is_published && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 border-amber-200">Draft</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">{article.category}</Badge>
                      <Badge variant="outline" className="text-xs bg-slate-50">
                        {article.audience === 'all' ? 'All Audiences' : article.audience === 'school' ? 'Schools' : article.audience === 'church' ? 'Churches' : article.audience}
                      </Badge>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Eye className="w-3 h-3" />{article.views || 0}</span>
                    </div>
                    {article.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {article.tags.map(t => <span key={t} className="text-xs text-slate-400">#{t}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => handleTogglePublish(article)} title={article.is_published ? 'Unpublish' : 'Publish'}>
                      <Eye className={cn("w-4 h-4", article.is_published ? 'text-emerald-500' : 'text-slate-300')} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(article)} data-testid={`kb-edit-${article.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(article.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

const PlatformAdminDashboard = () => {
  const { api, user, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeSection, setActiveSectionState] = useState(searchParams.get('tab') || 'overview');
  // Keep the URL (?tab=...) in sync so deep-links like /admin?tab=newsletters work.
  const setActiveSection = (section) => {
    setActiveSectionState(section);
    const next = new URLSearchParams(searchParams);
    if (section && section !== 'overview') next.set('tab', section);
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };
  // Follow any external URL change (e.g., clicking a task link that changes tab= param).
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeSection) setActiveSectionState(tab);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Scheduled Reports state
  const [scheduledReports, setScheduledReports] = useState([]);
  const [createReportDialog, setCreateReportDialog] = useState(false);
  const [newReport, setNewReport] = useState({
    name: '', report_type: 'weekly_usage', frequency: 'weekly',
    recipients: '', day_of_week: 0, hour: 9, is_active: true,
    filters: { organization_id: '' }
  });
  
  // Tier Features state
  const [tierFeatures, setTierFeatures] = useState([]);
  const [editTierDialog, setEditTierDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  
  // Bulk selection for organizations
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  
  // PO Edit state
  const [editPODialog, setEditPODialog] = useState(false);
  const [editPOData, setEditPOData] = useState(null);

  // Settings state
  const [settingsTab, setSettingsTab] = useState('status');
  const [systemStatus, setSystemStatus] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsPage, setAuditLogsPage] = useState(1);
  const [auditLogsTotal, setAuditLogsTotal] = useState(0);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsPage, setEmailLogsPage] = useState(1);
  const [emailLogsTotal, setEmailLogsTotal] = useState(0);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(null);

  // Promo Codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [createPromoDialog, setCreatePromoDialog] = useState(false);
  const [editPromoDialog, setEditPromoDialog] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [newPromo, setNewPromo] = useState({
    code: '', discount_type: 'percentage', discount_value: 10,
    expiration_date: '', max_uses: '', first_time_only: false,
    applicable_plans: [], description: '',
    duration: 'once', duration_in_months: 3,
  });

  // Announcements state moved to AnnouncementsSection component

  // Referral Program state
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [referralSettings, setReferralSettings] = useState(null);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [editReferralSettingsDialog, setEditReferralSettingsDialog] = useState(false);
  const [createReferralDialog, setCreateReferralDialog] = useState(false);
  const [selectedUserForReferral, setSelectedUserForReferral] = useState('');
  const [creatingReferral, setCreatingReferral] = useState(false);

  // Inactivity Reminders state
  const [inactiveChildren, setInactiveChildren] = useState([]);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderDaysThreshold, setReminderDaysThreshold] = useState(3);

  // QR Campaign state
  const [qrCampaigns, setQrCampaigns] = useState([]);
  const [qrLoading, setQrLoading] = useState(false);

  // Christian Education state
  const [christianStats, setChristianStats] = useState(null);
  const [christianUsers, setChristianUsers] = useState([]);
  const [christianChildren, setChristianChildren] = useState([]);
  const [christianLoading, setChristianLoading] = useState(false);

  // Fetch system status
  const fetchSystemStatus = async () => {
    setSettingsLoading(true);
    try {
      const response = await api.get('/admin/system-status');
      setSystemStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch system status:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch promo codes
  const fetchPromoCodes = async () => {
    setPromoLoading(true);
    try {
      const response = await api.get('/promo-codes');
      setPromoCodes(response.data.promo_codes || []);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
    } finally {
      setPromoLoading(false);
    }
  };

  // Fetch revenue data
  // Fetch Christian Education stats
  const fetchChristianStats = async () => {
    setChristianLoading(true);
    try {
      const [statsRes, usersRes, childrenRes] = await Promise.all([
        api.get('/admin/christian/stats'),
        api.get('/admin/christian/users?limit=50'),
        api.get('/admin/christian/children?limit=50')
      ]);
      setChristianStats(statsRes.data);
      setChristianUsers(usersRes.data.users || []);
      setChristianChildren(childrenRes.data.children || []);
    } catch (error) {
      console.error('Failed to fetch Christian stats:', error);
    } finally {
      setChristianLoading(false);
    }
  };


  // Create promo code
  const handleCreatePromo = async () => {
    try {
      const promoData = {
        ...newPromo,
        expiration_date: newPromo.expiration_date || null,
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        applicable_plans: newPromo.applicable_plans.length > 0 ? newPromo.applicable_plans : null,
        duration: newPromo.duration || 'once',
        duration_in_months: newPromo.duration === 'repeating'
          ? Math.max(1, parseInt(newPromo.duration_in_months) || 1)
          : null,
      };
      await api.post('/promo-codes', promoData);
      toast.success('Promo code created!');
      setCreatePromoDialog(false);
      setNewPromo({
        code: '', discount_type: 'percentage', discount_value: 10,
        expiration_date: '', max_uses: '', first_time_only: false,
        applicable_plans: [], description: '',
        duration: 'once', duration_in_months: 3,
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  // Update promo code
  const handleUpdatePromo = async () => {
    if (!editingPromo) return;
    try {
      await api.put(`/promo-codes/${editingPromo.code}`, editingPromo);
      toast.success('Promo code updated!');
      setEditPromoDialog(false);
      setEditingPromo(null);
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update promo code');
    }
  };

  // Delete promo code
  const handleDeletePromo = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"?`)) return;
    try {
      await api.delete(`/promo-codes/${code}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  // Announcement helpers moved to AnnouncementsSection component


  // ============ Referral Program helpers ============
  const fetchReferrals = async () => {
    setReferralsLoading(true);
    try {
      const [refs, stats, settings] = await Promise.all([
        api.get('/admin/referrals').catch(() => ({ data: { referrals: [] } })),
        api.get('/admin/referrals/stats').catch(() => ({ data: null })),
        api.get('/admin/referrals/settings').catch(() => ({ data: null })),
      ]);
      setReferrals(Array.isArray(refs.data) ? refs.data : (refs.data?.referrals || []));
      if (stats.data) setReferralStats(stats.data);
      if (settings.data) setReferralSettings(settings.data);
    } catch (err) {
      console.error('Failed to fetch referrals:', err);
      toast.error('Failed to load referral data');
    } finally {
      setReferralsLoading(false);
    }
  };

  const handleToggleReferral = async (referralId, currentActive) => {
    try {
      await api.put(`/admin/referrals/${referralId}/toggle`);
      toast.success(currentActive ? 'Referral paused' : 'Referral activated');
      fetchReferrals();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to toggle referral');
    }
  };

  const handleUpdateReferralSettings = async () => {
    if (!referralSettings) return;
    try {
      await api.put('/admin/referrals/settings', referralSettings);
      toast.success('Program settings saved');
      setEditReferralSettingsDialog(false);
      fetchReferrals();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save settings');
    }
  };

  const handleCreateReferralForUser = async () => {
    if (!selectedUserForReferral) return;
    setCreatingReferral(true);
    try {
      await api.post('/admin/referrals/generate-for-user', { user_id: selectedUserForReferral });
      toast.success('Referral link generated');
      setCreateReferralDialog(false);
      setSelectedUserForReferral('');
      fetchReferrals();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate referral');
    } finally {
      setCreatingReferral(false);
    }
  };

  // ============ Inactivity Reminders helpers ============
  const fetchInactiveChildren = async () => {
    setInactiveLoading(true);
    try {
      const res = await api.get(`/admin/inactive-children?days=${reminderDaysThreshold}`);
      const list = res.data?.inactive_children || res.data?.children || (Array.isArray(res.data) ? res.data : []);
      setInactiveChildren(list);
    } catch (err) {
      console.error('Failed to fetch inactive children:', err);
    } finally {
      setInactiveLoading(false);
    }
  };

  const handleSendReminder = async (childId, sendAll = false) => {
    setSendingReminder(true);
    try {
      const payload = sendAll
        ? { send_all: true, days_threshold: reminderDaysThreshold }
        : { child_id: childId };
      const res = await api.post('/admin/send-inactivity-reminder', payload);
      const count = res.data?.sent || res.data?.reminders_sent || (sendAll ? undefined : 1);
      toast.success(sendAll ? `Sent ${count ?? ''} reminders` : 'Reminder sent');
      fetchInactiveChildren();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };


  // QR Campaign functions
  const fetchQrCampaigns = async () => {
    setQrLoading(true);
    try {
      const response = await api.get('/admin/qr-campaigns');
      setQrCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch QR campaigns:', error);
    } finally {
      setQrLoading(false);
    }
  };


  // Fetch audit logs
  const fetchAuditLogs = async (page = 1) => {
    setSettingsLoading(true);
    try {
      const response = await api.get(`/admin/audit-logs?page=${page}&limit=20`);
      setAuditLogs(response.data.logs || []);
      setAuditLogsTotal(response.data.total || 0);
      setAuditLogsPage(page);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch email logs
  const fetchEmailLogs = async (page = 1) => {
    setSettingsLoading(true);
    try {
      const response = await api.get(`/admin/email-logs?page=${page}&limit=20`);
      setEmailLogs(response.data.logs || []);
      setEmailLogsTotal(response.data.total || 0);
      setEmailLogsPage(page);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Resend verification email
  const handleResendVerification = async (userId, userName) => {
    setResendingVerification(userId);
    try {
      const response = await api.post(`/admin/resend-verification/${userId}`);
      if (response.data.success) {
        toast.success(`Verification email sent to ${userName}`);
      } else {
        toast.error(response.data.message || 'Failed to send verification email');
      }
      fetchEmailLogs();
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setResendingVerification(null);
    }
  };

  // Fetch scheduled reports
  const fetchScheduledReports = async () => {
    try {
      const response = await api.get('/admin/scheduled-reports');
      setScheduledReports(response.data || []);
    } catch (error) {
      console.error('Failed to fetch scheduled reports:', error);
    }
  };

  // Fetch tier features
  const fetchTierFeatures = async () => {
    try {
      const response = await api.get('/admin/pricing/tier-features');
      setTierFeatures(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tier features:', error);
    }
  };

  // Create scheduled report
  const createScheduledReport = async () => {
    try {
      const recipients = newReport.recipients.split(',').map(e => e.trim()).filter(e => e);
      const reportData = {
        ...newReport,
        recipients,
        filters: newReport.filters?.organization_id ? { organization_id: newReport.filters.organization_id } : {}
      };
      await api.post('/admin/scheduled-reports', reportData);
      toast.success('Scheduled report created!');
      setCreateReportDialog(false);
      setNewReport({ name: '', report_type: 'weekly_usage', frequency: 'weekly', recipients: '', day_of_week: 0, hour: 9, is_active: true, filters: { organization_id: '' } });
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to create report');
    }
  };

  // Send report now
  const sendReportNow = async (reportId) => {
    try {
      const response = await api.post(`/admin/scheduled-reports/${reportId}/send-now`);
      toast.success(response.data.message);
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to send report');
    }
  };

  // Update tier features
  const updateTierFeatures = async () => {
    if (!editingTier) return;
    try {
      await api.put(`/admin/pricing/tier-features/${editingTier.tier_id}`, editingTier);
      toast.success('Tier features updated!');
      setEditTierDialog(false);
      fetchTierFeatures();
    } catch (error) {
      toast.error('Failed to update tier features');
    }
  };

  // Bulk operations for organizations
  const toggleOrgSelection = (orgId) => {
    setSelectedOrgs(prev => 
      prev.includes(orgId) ? prev.filter(id => id !== orgId) : [...prev, orgId]
    );
  };

  const selectAllOrgs = () => {
    if (selectedOrgs.length === organizations.length) {
      setSelectedOrgs([]);
    } else {
      setSelectedOrgs(organizations.map(o => o.id));
    }
  };

  const bulkUpdateOrgs = async (updateData) => {
    if (selectedOrgs.length === 0) {
      toast.error('No organizations selected');
      return;
    }
    try {
      await api.post('/admin/organizations/bulk-update', {
        org_ids: selectedOrgs,
        ...updateData
      });
      toast.success(`Updated ${selectedOrgs.length} organizations`);
      setSelectedOrgs([]);
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to update organizations');
    }
  };

  // Add fetch functions for quotes and purchase orders
  const fetchQuotes = async () => {
    try {
      const response = await api.get('/admin/quotes');
      setQuotes(response.data.quotes || []);
      setQuotesStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const response = await api.get('/admin/purchase-orders');
      setPurchaseOrders(response.data.purchase_orders || []);
      setPoStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch purchase orders:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await api.get('/admin/leads');
      setLeads(response.data.leads || []);
      setLeadsStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const generateQuote = async () => {
    try {
      const response = await api.post('/admin/quotes/generate', newQuote);
      toast.success(`Quote ${response.data.quote_number} generated!`);
      setCreateQuoteDialog(false);
      setNewQuote({
        organization_name: '', contact_name: '', contact_email: '', phone: '',
        student_count: 50, plan: 'enterprise', billing_cycle: 'annual', discount_percent: 0, notes: ''
      });
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to generate quote');
    }
  };

  const generatePurchaseOrder = async () => {
    try {
      const response = await api.post('/admin/purchase-orders/generate', newPO);
      toast.success(`PO ${response.data.po_number} generated!`);
      setCreatePODialog(false);
      setNewPO({
        organization_name: '', billing_address: '', contact_name: '', contact_email: '',
        phone: '', student_count: 50, plan: 'enterprise', billing_cycle: 'annual', payment_terms: 'NET30', notes: ''
      });
      fetchPurchaseOrders();
    } catch (error) {
      toast.error('Failed to generate purchase order');
    }
  };

  const handleEditPO = async () => {
    if (!editPOData) return;
    try {
      await api.put(`/admin/purchase-orders/${editPOData.id}`, editPOData);
      toast.success('Purchase order updated!');
      setEditPODialog(false);
      setEditPOData(null);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update purchase order');
    }
  };

  const handleDeletePO = async (poId) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;
    try {
      await api.delete(`/admin/purchase-orders/${poId}`);
      toast.success('Purchase order deleted');
      fetchPurchaseOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete purchase order');
    }
  };

  const sendQuoteEmail = async (quoteId) => {
    try {
      await api.post(`/admin/quotes/${quoteId}/send`);
      toast.success('Quote sent!');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await api.put(`/admin/quotes/${quoteId}/status`, null, { params: { status } });
      toast.success('Quote status updated');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to update quote status');
    }
  };

  const updatePOStatus = async (poId, status) => {
    try {
      await api.put(`/admin/purchase-orders/${poId}/status`, null, { params: { status } });
      toast.success('PO status updated');
      fetchPurchaseOrders();
    } catch (error) {
      toast.error('Failed to update PO status');
    }
  };
  
  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [archivedOrgs, setArchivedOrgs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });
  const [faqArticles, setFaqArticles] = useState([]);
  const [faqStats, setFaqStats] = useState({ total: 0, published: 0, draft: 0 });
  const [pricingPlans, setPricingPlans] = useState([]);
  
  // Pricing Plans edit states
  const [editPlanDialog, setEditPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [createPlanDialog, setCreatePlanDialog] = useState(false);
  const [newPlan, setNewPlan] = useState({ plan_id: '', name: '', price: 0, features: '', plan_type: 'individual' });
  const [savingPlan, setSavingPlan] = useState(false);
  
  // Team/Employee states
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamStats, setTeamStats] = useState({ total: 0, active: 0 });
  const [teamLoading, setTeamLoading] = useState(false);
  const [addEmployeeDialog, setAddEmployeeDialog] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: '', email: '', password: '', role: 'support', department: 'support' });
  const [addingEmployee, setAddingEmployee] = useState(false);
  
  // License requests states
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [licenseRequestStats, setLicenseRequestStats] = useState({ total: 0, pending: 0 });
  const [licenseRequestsLoading, setLicenseRequestsLoading] = useState(false);
  const [processRequestDialog, setProcessRequestDialog] = useState({ open: false, request: null });
  const [adminNotes, setAdminNotes] = useState('');
  const [processingRequest, setProcessingRequest] = useState(false);
  
  // Leads, Quotes, Purchase Orders states
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [quotesStats, setQuotesStats] = useState({});
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poStats, setPoStats] = useState({});
  const [createQuoteDialog, setCreateQuoteDialog] = useState(false);
  const [createPODialog, setCreatePODialog] = useState(false);
  const [newQuote, setNewQuote] = useState({
    organization_name: '', contact_name: '', contact_email: '', phone: '',
    student_count: 50, plan: 'enterprise', billing_cycle: 'annual', discount_percent: 0, notes: ''
  });
  const [newPO, setNewPO] = useState({
    organization_name: '', billing_address: '', contact_name: '', contact_email: '',
    phone: '', student_count: 50, plan: 'enterprise', billing_cycle: 'annual', payment_terms: 'NET30', notes: ''
  });
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [sendingWeeklyReports, setSendingWeeklyReports] = useState(false);
  
  // Enhanced Users states
  const [groupedUsers, setGroupedUsers] = useState({ personal: { users: [], count: 0 }, organizations: [], stats: {} });
  const [userViewMode, setUserViewMode] = useState('grouped'); // grouped, all
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkUpdateDialog, setBulkUpdateDialog] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({ subscription_plan: '', account_type: '' });
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [userGroupFilter, setUserGroupFilter] = useState('all'); // all, personal, org
  const [expandedOrgs, setExpandedOrgs] = useState({});
  
  // Filter states
  const [userSearch, setUserSearch] = useState('');
  const [orgSearch, setOrgSearch] = useState('');
  const [orgTypeFilter, setOrgTypeFilter] = useState('all');
  const [ticketFilter, setTicketFilter] = useState({ status: 'all', priority: 'all' });
  const [userPage, setUserPage] = useState(1);
  const [totalUserPages, setTotalUserPages] = useState(1);
  
  // Dialog states
  const [createOrgDialog, setCreateOrgDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState({ open: false, user: null });
  const [editOrgDialog, setEditOrgDialog] = useState({ open: false, org: null });
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, userId: null, userName: '' });
  const [archiveOrgDialog, setArchiveOrgDialog] = useState({ open: false, orgId: null, orgName: '', isArchived: false });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResponse, setTicketResponse] = useState('');
  const [showNewFaqForm, setShowNewFaqForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, userId: null, userName: '' });
  const [newPassword, setNewPassword] = useState('');
  const [savingUser, setSavingUser] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  
  // Form states
  const [newOrg, setNewOrg] = useState({
    name: '', 
    organization_type: 'school', 
    contact_email: '', 
    contact_phone: '', 
    student_licenses: 50,
    admin_email: '',
    admin_name: '',
    admin_password: '',
    google_classroom_enabled: false
  });
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', category: 'general', order: 0, is_published: true });
  
  // Loading action states
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [archivingOrg, setArchivingOrg] = useState(false);
  const [savingFaq, setSavingFaq] = useState(false);
  const [respondingTicket, setRespondingTicket] = useState(false);

  useEffect(() => {
    fetchInitialData();
    fetchLicenseRequests(); // Fetch on load for badge count
  }, []);

  useEffect(() => {
    if (activeSection === 'users') fetchUsers();
    if (activeSection === 'organizations') fetchOrganizations();
    if (activeSection === 'archived') fetchArchivedOrgs();
    if (activeSection === 'support') fetchTickets();
    if (activeSection === 'faq') fetchFaq();
    if (activeSection === 'pricing') fetchPricing();
    if (activeSection === 'team') fetchTeam();
    if (activeSection === 'license-requests') fetchLicenseRequests();
    if (activeSection === 'quotes') fetchQuotes();
    if (activeSection === 'purchase-orders') fetchPurchaseOrders();
    if (activeSection === 'leads') fetchLeads();
    if (activeSection === 'scheduled-reports') {
      fetchScheduledReports();
      fetchOrganizations(); // Need orgs for the report filter
    }
    if (activeSection === 'tier-features') fetchTierFeatures();
    if (activeSection === 'promo-codes') fetchPromoCodes();
    // announcements: self-fetches inside AnnouncementsSection component
    if (activeSection === 'referrals') {
      fetchReferrals();
      fetchUsers(); // For creating referrals for users
      fetchInactiveChildren();
      fetchQrCampaigns();
    }
    if (activeSection === 'christian') {
      fetchChristianStats();
    }
    if (activeSection === 'settings') {
      fetchSystemStatus();
      fetchAuditLogs();
      fetchEmailLogs();
      fetchUsers(); // For resend verification
    }
  }, [activeSection]);

  const fetchInitialData = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Only kick out true non-employees. Staff/interns are allowed on /admin (they
      // just get 403 from admin-only endpoints; we swallow silently).
      const isStaffOrIntern = !!(user?.is_staff || user?.intern_role);
      if (error.response?.status === 403 && !isStaffOrIntern) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  // Team/Employee functions
  const fetchTeam = async () => {
    setTeamLoading(true);
    try {
      const response = await api.get('/admin/team');
      // The backend endpoint (careers.py) returns { members: [...], stats: {...} }.
      // Normalize into the schema this table expects (role/department/is_active).
      const raw = response.data.members || response.data.employees || [];
      const _deptOfRole = (roleKey) => {
        const k = String(roleKey || '').toLowerCase();
        if (k.includes('support')) return 'support';
        if (['engineer', 'developer', 'dev'].some((s) => k.includes(s))) return 'engineering';
        if (['hr', 'recruit', 'hiring', 'people'].some((s) => k.includes(s))) return 'hr';
        if (['content', 'curriculum', 'writer', 'editor'].some((s) => k.includes(s))) return 'content';
        if (['market', 'social'].some((s) => k.includes(s))) return 'marketing';
        return 'operations';
      };
      const normalized = raw.map((m) => ({
        ...m,
        role: m.role || m.intern_role_label || m.intern_role || (m.is_admin ? 'admin' : 'staff'),
        department: m.department || _deptOfRole(m.intern_role || m.role),
        is_active: m.is_active !== false,
      }));
      // Rebuild stats.by_department if backend didn't send it (careers.py doesn't).
      const backendStats = response.data.stats || {};
      const by_department = backendStats.by_department || normalized.reduce((acc, e) => {
        const d = e.department || 'other';
        acc[d] = (acc[d] || 0) + 1;
        return acc;
      }, {});
      setTeamMembers(normalized);
      setTeamStats({
        total: backendStats.total ?? normalized.length,
        active: backendStats.active ?? normalized.filter((e) => e.is_active !== false).length,
        by_department,
        admins: backendStats.admins ?? normalized.filter((e) => e.is_admin).length,
        interns: backendStats.interns ?? normalized.filter((e) => e.intern_role).length,
        staff: backendStats.staff ?? normalized.filter((e) => e.is_staff && !e.intern_role && !e.is_admin).length,
      });
    } catch (error) {
      console.error('Failed to fetch team:', error);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.email || !newEmployee.password) {
      toast.error('Please fill all required fields');
      return;
    }
    setAddingEmployee(true);
    try {
      await api.post('/admin/team', newEmployee);
      toast.success('Employee added successfully');
      setAddEmployeeDialog(false);
      setNewEmployee({ name: '', email: '', password: '', role: 'support', department: 'support' });
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    } finally {
      setAddingEmployee(false);
    }
  };

  const handleUpdateEmployee = async (employeeId, updates) => {
    try {
      await api.put(`/admin/team/${employeeId}`, updates);
      toast.success('Employee updated');
      fetchTeam();
    } catch (error) {
      toast.error('Failed to update employee');
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to remove this employee?')) return;
    try {
      await api.delete(`/admin/team/${employeeId}`);
      toast.success('Employee removed');
      fetchTeam();
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };

  // License request functions
  const fetchLicenseRequests = async () => {
    setLicenseRequestsLoading(true);
    try {
      const response = await api.get('/admin/license-requests');
      setLicenseRequests(response.data.requests || []);
      setLicenseRequestStats(response.data.stats || { total: 0, pending: 0 });
    } catch (error) {
      console.error('Failed to fetch license requests:', error);
    } finally {
      setLicenseRequestsLoading(false);
    }
  };

  const handleProcessLicenseRequest = async (action) => {
    setProcessingRequest(true);
    try {
      await api.put(`/admin/license-requests/${processRequestDialog.request.id}`, {
        action,
        admin_notes: adminNotes
      });
      toast.success(`License request ${action}d`);
      setProcessRequestDialog({ open: false, request: null });
      setAdminNotes('');
      fetchLicenseRequests();
    } catch (error) {
      toast.error(`Failed to ${action} request`);
    } finally {
      setProcessingRequest(false);
    }
  };

  // Send weekly reports
  const handleSendWeeklyReports = async () => {
    setSendingWeeklyReports(true);
    try {
      const response = await api.post('/admin/send-weekly-reports');
      if (response.data.success) {
        toast.success(`Sent ${response.data.sent} reports`);
        if (response.data.failed > 0) {
          toast.warning(`${response.data.failed} reports failed`);
        }
      } else {
        toast.error(response.data.message || 'Failed to send reports');
      }
    } catch (error) {
      toast.error('Failed to send weekly reports');
    } finally {
      setSendingWeeklyReports(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      if (userViewMode === 'grouped') {
        const response = await api.get(`/admin/users/grouped?search=${userSearch}`);
        setGroupedUsers(response.data);
        // Also set flat users for editing
        const allUsers = [
          ...(response.data.personal?.users || []),
          ...response.data.organizations.flatMap(org => org.users || [])
        ];
        setUsers(allUsers);
      } else {
        const response = await api.get(`/admin/users?page=${userPage}&search=${userSearch}`);
        setUsers(response.data.users || []);
        setTotalUserPages(response.data.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    const updates = {};
    if (bulkUpdateData.subscription_plan) updates.subscription_plan = bulkUpdateData.subscription_plan;
    if (bulkUpdateData.account_type) updates.account_type = bulkUpdateData.account_type;
    
    if (Object.keys(updates).length === 0) {
      toast.error('No updates specified');
      return;
    }
    
    setBulkUpdating(true);
    try {
      const response = await api.put('/admin/users/bulk-update', {
        user_ids: selectedUsers,
        updates
      });
      toast.success(`Updated ${response.data.modified_count} users`);
      setBulkUpdateDialog(false);
      setSelectedUsers([]);
      setBulkUpdateData({ subscription_plan: '', account_type: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update users');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} users? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await api.delete('/admin/users/bulk-delete', { data: selectedUsers });
      toast.success(`Deleted ${response.data.deleted_count} users`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = (userList) => {
    const allIds = userList.map(u => u.id);
    const allSelected = allIds.every(id => selectedUsers.includes(id));
    
    if (allSelected) {
      setSelectedUsers(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedUsers(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const toggleOrgExpanded = (orgId) => {
    setExpandedOrgs(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }));
  };

  const fetchOrganizations = async () => {
    setOrgsLoading(true);
    try {
      const response = await api.get('/admin/organizations');
      const allOrgs = response.data.organizations || [];
      setOrganizations(allOrgs.filter(o => !o.is_archived));
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setOrgsLoading(false);
    }
  };

  const fetchArchivedOrgs = async () => {
    setOrgsLoading(true);
    try {
      const response = await api.get('/admin/organizations?include_archived=true');
      const allOrgs = response.data.organizations || [];
      setArchivedOrgs(allOrgs.filter(o => o.is_archived));
    } catch (error) {
      console.error('Failed to fetch archived organizations:', error);
    } finally {
      setOrgsLoading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      const params = new URLSearchParams();
      if (ticketFilter.status !== 'all') params.append('status', ticketFilter.status);
      if (ticketFilter.priority !== 'all') params.append('priority', ticketFilter.priority);
      
      const response = await api.get(`/admin/support/tickets?${params.toString()}`);
      setTickets(response.data.tickets || []);
      setTicketStats(response.data.stats || { total: 0, open: 0, in_progress: 0, resolved: 0 });
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    }
  };

  const fetchFaq = async () => {
    try {
      const response = await api.get('/admin/faq');
      setFaqArticles(response.data.articles || []);
      setFaqStats(response.data.stats || { total: 0, published: 0, draft: 0 });
    } catch (error) {
      console.error('Failed to fetch FAQ:', error);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await api.get('/admin/pricing/plans');
      // Admin endpoint returns array directly with features as array
      const data = response.data;
      if (Array.isArray(data)) {
        // Convert features array to string for display, keep original for editing
        const plansWithStringFeatures = data.map(plan => ({
          ...plan,
          featuresArray: Array.isArray(plan.features) ? plan.features : [],
          features: Array.isArray(plan.features) ? plan.features.join(', ') : plan.features
        }));
        setPricingPlans(plansWithStringFeatures);
      } else if (data && typeof data === 'object') {
        // Handle object format from public endpoint
        const plansArray = Object.entries(data).map(([plan_id, plan]) => ({
          plan_id,
          ...plan,
          featuresArray: Array.isArray(plan.features) ? plan.features : [],
          features: Array.isArray(plan.features) ? plan.features.join(', ') : plan.features
        }));
        setPricingPlans(plansArray);
      } else {
        setPricingPlans([]);
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  // Pricing Plan CRUD
  const handleCreatePlan = async () => {
    if (!newPlan.plan_id || !newPlan.name || !newPlan.price) {
      toast.error('Please fill all required fields');
      return;
    }
    setSavingPlan(true);
    try {
      const features = newPlan.features.split(',').map(f => f.trim()).filter(f => f);
      
      // Build query string manually to properly handle arrays
      const params = new URLSearchParams();
      params.append('plan_id', newPlan.plan_id);
      params.append('name', newPlan.name);
      params.append('price', newPlan.price);
      features.forEach(f => params.append('features', f));
      params.append('plan_type', newPlan.plan_type);
      
      await api.post(`/admin/pricing/plans?${params.toString()}`);
      toast.success('Plan created successfully!');
      setCreatePlanDialog(false);
      setNewPlan({ plan_id: '', name: '', price: 0, features: '', plan_type: 'individual' });
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan) return;
    setSavingPlan(true);
    try {
      const features = typeof editingPlan.features === 'string' 
        ? editingPlan.features.split(',').map(f => f.trim()).filter(f => f)
        : editingPlan.features || [];
      
      // Build query string manually to properly handle arrays
      const params = new URLSearchParams();
      params.append('name', editingPlan.name);
      params.append('price', editingPlan.price);
      features.forEach(f => params.append('features', f));
      if (editingPlan.is_active !== undefined) {
        params.append('is_active', editingPlan.is_active);
      }
      
      await api.put(`/admin/pricing/plans/${editingPlan.plan_id}?${params.toString()}`);
      toast.success('Plan updated successfully!');
      setEditPlanDialog(false);
      setEditingPlan(null);
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this pricing plan?')) return;
    try {
      await api.delete(`/admin/pricing/plans/${planId}`);
      toast.success('Plan deleted');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  // Organization CRUD
  const handleCreateOrganization = async () => {
    if (!newOrg.name || !newOrg.contact_email || !newOrg.admin_email || !newOrg.admin_name || !newOrg.admin_password) {
      toast.error('Please fill all required fields');
      return;
    }
    if (newOrg.admin_password.length < 6) {
      toast.error('Admin password must be at least 6 characters');
      return;
    }
    setCreatingOrg(true);
    try {
      await api.post('/admin/organizations', newOrg);
      toast.success('Organization created successfully');
      setCreateOrgDialog(false);
      setNewOrg({ 
        name: '', 
        organization_type: 'school', 
        contact_email: '', 
        contact_phone: '', 
        student_licenses: 50,
        admin_email: '',
        admin_name: '',
        admin_password: '',
        google_classroom_enabled: false
      });
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleArchiveOrganization = async () => {
    setArchivingOrg(true);
    try {
      const action = archiveOrgDialog.isArchived ? 'unarchive' : 'archive';
      await api.put(`/admin/organizations/${archiveOrgDialog.orgId}/${action}`);
      toast.success(`Organization ${action}d successfully`);
      setArchiveOrgDialog({ open: false, orgId: null, orgName: '', isArchived: false });
      fetchOrganizations();
      fetchArchivedOrgs();
    } catch (error) {
      toast.error('Failed to archive/unarchive organization');
    } finally {
      setArchivingOrg(false);
    }
  };

  const handleDeleteOrganization = async (orgId) => {
    if (!window.confirm('Are you sure? This will permanently delete the organization and all its data.')) return;
    try {
      await api.delete(`/admin/organizations/${orgId}`);
      toast.success('Organization deleted');
      fetchOrganizations();
      fetchArchivedOrgs();
    } catch (error) {
      toast.error('Failed to delete organization');
    }
  };

  // Toggle Bible Curriculum for Organization
  const handleToggleOrgBibleCurriculum = async (orgId, enabled) => {
    try {
      await api.put(`/admin/organizations/${orgId}/bible-curriculum`, { enabled });
      toast.success(`Bible curriculum ${enabled ? 'enabled' : 'disabled'}`);
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to update Bible curriculum setting');
    }
  };

  // Toggle Bible Curriculum for User
  const handleToggleUserBibleCurriculum = async (userId, enabled) => {
    try {
      await api.put(`/admin/users/${userId}/bible-curriculum`, { enabled });
      toast.success(`Bible curriculum ${enabled ? 'enabled' : 'disabled'} for user`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update Bible curriculum setting');
    }
  };

  // Toggle Christian Education for User
  const handleToggleUserChristianEducation = async (userId, enabled) => {
    try {
      await api.put(`/admin/users/${userId}`, { christian_education_enabled: enabled });
      toast.success(`Christian Education ${enabled ? 'enabled' : 'disabled'} for user`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update Christian Education setting');
    }
  };

  // User CRUD
  const handleUpdateUser = async (userId, updates) => {
    setSavingUser(true);
    try {
      await api.put(`/admin/users/${userId}`, updates);
      toast.success('User updated');
      fetchUsers();
      setEditUserDialog({ open: false, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleResetPassword = async () => {
    if (!isPasswordValid(newPassword || '')) {
      toast.error('Password must include upper, lower, number, and special character (min 8).');
      return;
    }
    setSavingUser(true);
    try {
      await api.put(`/admin/users/${resetPasswordDialog.userId}/password`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setResetPasswordDialog({ open: false, userId: null, userName: '' });
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSavingUser(false);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!editOrgDialog.org) return;
    setSavingOrg(true);
    try {
      const { id, name, organization_type, contact_email, contact_phone, student_licenses } = editOrgDialog.org;
      await api.put(`/admin/organizations/${id}`, null, {
        params: {
          name,
          organization_type,
          contact_email,
          contact_phone,
          student_licenses
        }
      });
      toast.success('Organization updated');
      fetchOrganizations();
      setEditOrgDialog({ open: false, org: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${deleteUserDialog.userId}`);
      toast.success('User deleted');
      setDeleteUserDialog({ open: false, userId: null, userName: '' });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  // Impersonate a user
  const handleImpersonateUser = async (userId, userName) => {
    try {
      await impersonateUser(userId, false); // false = platform admin
      toast.success(`Now viewing as ${userName}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  // Apply promo code to user
  const [applyPromoDialog, setApplyPromoDialog] = useState(false);
  const [applyPromoUserId, setApplyPromoUserId] = useState(null);
  const [applyPromoUserEmail, setApplyPromoUserEmail] = useState('');
  const [applyPromoCode, setApplyPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  const handleApplyPromoToUser = async () => {
    if (!applyPromoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    setApplyingPromo(true);
    try {
      const response = await api.post(`/promo-codes/apply/${applyPromoUserId}?code=${applyPromoCode.toUpperCase()}`);
      toast.success(`Promo code applied to ${applyPromoUserEmail}! ${response.data.stripe_applied ? '(Stripe subscription updated)' : ''}`);
      setApplyPromoDialog(false);
      setApplyPromoCode('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply promo code');
    } finally {
      setApplyingPromo(false);
    }
  };

  const openApplyPromoDialog = (userId, userEmail) => {
    setApplyPromoUserId(userId);
    setApplyPromoUserEmail(userEmail);
    setApplyPromoCode('');
    setApplyPromoDialog(true);
  };

  // Ticket handlers
  const handleUpdateTicket = async (ticketId, updates) => {
    try {
      await api.put(`/admin/support/tickets/${ticketId}`, updates);
      toast.success('Ticket updated');
      fetchTickets();
      setSelectedTicket(null);
      setTicketResponse('');
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleRespondToTicket = async () => {
    if (!ticketResponse.trim()) {
      toast.error('Please enter a response');
      return;
    }
    setRespondingTicket(true);
    try {
      await api.put(`/admin/support/tickets/${selectedTicket.id}`, {
        admin_response: ticketResponse,
        status: 'in_progress'
      });
      toast.success('Response sent');
      fetchTickets();
      setSelectedTicket(null);
      setTicketResponse('');
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setRespondingTicket(false);
    }
  };

  // FAQ handlers
  const handleCreateFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      toast.error('Please fill question and answer');
      return;
    }
    setSavingFaq(true);
    try {
      await api.post('/admin/faq', newFaq);
      toast.success('FAQ created');
      fetchFaq();
      setNewFaq({ question: '', answer: '', category: 'general', order: 0, is_published: true });
      setShowNewFaqForm(false);
    } catch (error) {
      toast.error('Failed to create FAQ');
    } finally {
      setSavingFaq(false);
    }
  };

  const handleUpdateFaq = async (faqId, updates) => {
    try {
      await api.put(`/admin/faq/${faqId}`, updates);
      toast.success('FAQ updated');
      fetchFaq();
      setEditingFaq(null);
    } catch (error) {
      toast.error('Failed to update FAQ');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await api.delete(`/admin/faq/${faqId}`);
      toast.success('FAQ deleted');
      fetchFaq();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const handleSeedFaq = async () => {
    try {
      const response = await api.post('/admin/faq/seed');
      toast.success(response.data.message || `Seeded ${response.data.seeded_count} FAQs`);
      fetchFaq();
    } catch (error) {
      toast.error('Failed to seed FAQs');
    }
  };

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const subscriptionData = stats ? [
    { name: 'Free', value: stats.subscriptions?.free || 0 },
    { name: 'Basic', value: stats.subscriptions?.basic || 0 },
    { name: 'Premium', value: stats.subscriptions?.premium || 0 },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-100">
      <PlatformAdminSidebar 
        collapsed={sidebarCollapsed} 
        setCollapsed={setSidebarCollapsed}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        pendingRequests={licenseRequestStats.pending || 0}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      {/* Mobile top bar */}
      <div
        className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-3 shadow-sm"
        data-testid="admin-mobile-topbar"
      >
        <button
          onClick={() => setMobileNavOpen(true)}
          className="p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-700"
          aria-label="Open menu"
          data-testid="admin-mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gradient-to-br from-teal-600 to-teal-700 rounded-lg flex items-center justify-center">
            <Crown className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-sm">Zazu Admin</span>
        </div>
        <div className="w-9" />
      </div>

      <main className={cn(
        "transition-all duration-300 p-4 md:p-6",
        sidebarCollapsed ? "md:ml-16" : "md:ml-64"
      )}>
        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Platform Overview</h1>
              <div className="flex gap-2">
                <ClearCacheButton testId="admin-clear-cache-btn" />
                <Button variant="outline" onClick={handleSendWeeklyReports} disabled={sendingWeeklyReports}>
                  {sendingWeeklyReports ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Weekly Reports
                </Button>
                <Button variant="outline" onClick={fetchInitialData}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('users')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Users</p>
                      <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('organizations')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Organizations</p>
                      <p className="text-2xl font-bold text-slate-900">{stats?.total_organizations || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('support')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Open Tickets</p>
                      <p className="text-2xl font-bold text-slate-900">{ticketStats.open || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('pricing')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Revenue</p>
                      <p className="text-2xl font-bold text-slate-900">${stats?.total_revenue || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={() => setActiveSection('license-requests')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center",
                      licenseRequestStats.pending > 0 ? "bg-red-100" : "bg-slate-100"
                    )}>
                      <ClipboardCheck className={cn(
                        "w-6 h-6",
                        licenseRequestStats.pending > 0 ? "text-red-600" : "text-slate-600"
                      )} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Pending Requests</p>
                      <p className={cn(
                        "text-2xl font-bold",
                        licenseRequestStats.pending > 0 ? "text-red-600" : "text-slate-900"
                      )}>{licenseRequestStats.pending || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Website Analytics Section */}
            <div className="mt-6">
              <AnalyticsWidget />
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Subscription Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={subscriptionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                        >
                          {subscriptionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" onClick={() => setActiveSection('organizations')}>
                    <Building2 className="w-4 h-4 mr-2" /> Manage Organizations
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setActiveSection('users')}>
                    <Users className="w-4 h-4 mr-2" /> Manage Users
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setActiveSection('support')}>
                    <MessageSquare className="w-4 h-4 mr-2" /> View Support Tickets
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setActiveSection('faq')}>
                    <HelpCircle className="w-4 h-4 mr-2" /> Manage Knowledge Base
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Organizations Section */}
        {activeSection === 'organizations' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Organizations</h1>
              <div className="flex gap-2">
                {selectedOrgs.length > 0 && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => bulkUpdateOrgs({ bible_curriculum_enabled: true })}
                      className="text-emerald-600"
                      data-testid="bulk-enable-bible"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Enable Bible ({selectedOrgs.length})
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => bulkUpdateOrgs({ bible_curriculum_enabled: false })}
                      className="text-slate-600"
                      data-testid="bulk-disable-bible"
                    >
                      <X className="w-4 h-4 mr-2" /> Disable Bible ({selectedOrgs.length})
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setActiveSection('archived')}>
                  <Archive className="w-4 h-4 mr-2" /> View Archived
                </Button>
                <Button onClick={() => setCreateOrgDialog(true)} data-testid="create-org-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create Organization
                </Button>
              </div>
            </div>
            
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-4 items-center">
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search organizations..."
                      value={orgSearch}
                      onChange={(e) => setOrgSearch(e.target.value)}
                      className="pl-10"
                      data-testid="org-search-input"
                    />
                  </div>
                  <Select value={orgTypeFilter} onValueChange={setOrgTypeFilter}>
                    <SelectTrigger className="w-40" data-testid="org-type-filter">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="school">School</SelectItem>
                      <SelectItem value="church">Church</SelectItem>
                      <SelectItem value="sunday_school">Sunday School</SelectItem>
                      <SelectItem value="homeschool_coop">Homeschool Co-op</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchOrganizations}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Organizations Table */}
            <Card>
              <CardContent className="p-0">
                {orgsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox 
                            checked={organizations.length > 0 && selectedOrgs.length === organizations.length}
                            onCheckedChange={selectAllOrgs}
                            data-testid="select-all-orgs"
                          />
                        </TableHead>
                        <TableHead>Account #</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-center">Users</TableHead>
                        <TableHead className="text-center">Students</TableHead>
                        <TableHead className="text-center">Licenses</TableHead>
                        <TableHead className="text-center">Bible</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {organizations.filter(org => 
                        org.name.toLowerCase().includes(orgSearch.toLowerCase()) &&
                        (orgTypeFilter === 'all' || org.organization_type === orgTypeFilter)
                      ).map((org) => (
                        <TableRow key={org.id} className={selectedOrgs.includes(org.id) ? "bg-emerald-50" : ""}>
                          <TableCell>
                            <Checkbox 
                              checked={selectedOrgs.includes(org.id)}
                              onCheckedChange={() => toggleOrgSelection(org.id)}
                              data-testid={`select-org-${org.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                              {org.account_number || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Link to={`/admin/organization/${org.id}`} className="flex items-center gap-3 hover:text-emerald-600">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                org.organization_type === 'church' || org.organization_type === 'sunday_school' 
                                  ? 'bg-purple-100' : 'bg-blue-100'
                              }`}>
                                {org.organization_type === 'church' || org.organization_type === 'sunday_school' 
                                  ? <Church className="w-5 h-5 text-purple-600" />
                                  : <School className="w-5 h-5 text-blue-600" />
                                }
                              </div>
                              <div>
                                <p className="font-medium">{org.name}</p>
                                <p className="text-xs text-slate-500">{org.contact_email}</p>
                              </div>
                            </Link>
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 rounded-full text-xs font-medium capitalize bg-slate-100 text-slate-700">
                              {org.organization_type?.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">{org.teacher_count || 0}</TableCell>
                          <TableCell className="text-center">{org.student_count || 0}</TableCell>
                          <TableCell className="text-center">
                            {org.student_count || 0}/{org.student_licenses || 0}
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={org.bible_curriculum_enabled}
                              onCheckedChange={(checked) => handleToggleOrgBibleCurriculum(org.id, checked)}
                              data-testid={`bible-toggle-org-${org.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Link to={`/admin/organization/${org.id}`}>
                                <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                              </Link>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => setEditOrgDialog({ open: true, org: { ...org } })}
                                data-testid={`edit-org-${org.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => setArchiveOrgDialog({ open: true, orgId: org.id, orgName: org.name, isArchived: false })}
                              >
                                <Archive className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-500"
                                onClick={() => handleDeleteOrganization(org.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Archived Organizations Section */}
        {activeSection === 'archived' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Archived Organizations</h1>
              <Button variant="outline" onClick={() => setActiveSection('organizations')}>
                Back to Organizations
              </Button>
            </div>
            
            <Card>
              <CardContent className="p-0">
                {orgsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : archivedOrgs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Archive className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No archived organizations</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Organization</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Archived Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedOrgs.map((org) => (
                        <TableRow key={org.id} className="opacity-75">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100">
                                <Archive className="w-5 h-5 text-slate-400" />
                              </div>
                              <div>
                                <p className="font-medium">{org.name}</p>
                                <p className="text-xs text-slate-500">{org.contact_email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">{org.organization_type?.replace('_', ' ')}</TableCell>
                          <TableCell>{org.archived_at ? new Date(org.archived_at).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => setArchiveOrgDialog({ open: true, orgId: org.id, orgName: org.name, isArchived: true })}
                              >
                                <ArchiveRestore className="w-4 h-4" /> Restore
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-500"
                                onClick={() => handleDeleteOrganization(org.id)}
                              >
                                <Trash2 className="w-4 h-4" /> Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Section */}
        {activeSection === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
              <div className="flex gap-2">
                {selectedUsers.length > 0 && (
                  <>
                    <Button variant="outline" onClick={() => setBulkUpdateDialog(true)}>
                      <Edit2 className="w-4 h-4 mr-2" /> Bulk Update ({selectedUsers.length})
                    </Button>
                    <Button variant="outline" className="text-red-600" onClick={handleBulkDelete}>
                      <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedUsers.length})
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={fetchUsers}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>
            </div>
            
            {/* Stats Cards */}
            <div className="grid sm:grid-cols-4 gap-4">
              <Card className={cn("cursor-pointer transition-all", userGroupFilter === 'all' && "ring-2 ring-emerald-500")}
                onClick={() => setUserGroupFilter('all')}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{groupedUsers.stats?.total || users.length}</p>
                  <p className="text-xs text-slate-500">Total Users</p>
                </CardContent>
              </Card>
              <Card className={cn("cursor-pointer transition-all", userGroupFilter === 'personal' && "ring-2 ring-blue-500")}
                onClick={() => setUserGroupFilter('personal')}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{groupedUsers.stats?.personal_count || 0}</p>
                  <p className="text-xs text-slate-500">Personal Users</p>
                </CardContent>
              </Card>
              <Card className={cn("cursor-pointer transition-all", userGroupFilter === 'org' && "ring-2 ring-purple-500")}
                onClick={() => setUserGroupFilter('org')}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{groupedUsers.stats?.org_count || 0}</p>
                  <p className="text-xs text-slate-500">Organization Users</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{groupedUsers.stats?.orgs_with_users || 0}</p>
                  <p className="text-xs text-slate-500">Organizations</p>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-4 items-center flex-wrap">
                  <div className="flex-1 min-w-64 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Select value={userViewMode} onValueChange={setUserViewMode}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="View Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grouped">Grouped View</SelectItem>
                      <SelectItem value="all">Flat List</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {usersLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : userViewMode === 'grouped' ? (
              <div className="space-y-4">
                {/* Personal Users Section */}
                {(userGroupFilter === 'all' || userGroupFilter === 'personal') && groupedUsers.personal?.users?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <User className="w-5 h-5 text-blue-500" />
                          Personal Users ({groupedUsers.personal.count})
                        </CardTitle>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => toggleSelectAll(groupedUsers.personal.users)}
                        >
                          {groupedUsers.personal.users.every(u => selectedUsers.includes(u.id)) 
                            ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12"></TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Account Type</TableHead>
                            <TableHead>Subscription</TableHead>
                            <TableHead className="text-center">Bible</TableHead>
                            <TableHead className="text-center">Christian Ed</TableHead>
                            <TableHead>Joined</TableHead>
                            <TableHead>Last Login</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {groupedUsers.personal.users.filter(u => 
                            !userSearch || 
                            u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
                            u.email?.toLowerCase().includes(userSearch.toLowerCase())
                          ).map((u) => (
                            <TableRow key={u.id} className={selectedUsers.includes(u.id) ? "bg-blue-50" : ""}>
                              <TableCell>
                                <Checkbox 
                                  checked={selectedUsers.includes(u.id)}
                                  onCheckedChange={() => toggleUserSelection(u.id)}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{u.name}</TableCell>
                              <TableCell>{u.email}</TableCell>
                              <TableCell className="capitalize">{u.account_type?.replace('_', ' ')}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  u.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-700' :
                                  u.subscription_plan === 'basic' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {u.subscription_plan || 'free'}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <Switch
                                  checked={u.bible_curriculum_enabled || false}
                                  onCheckedChange={(checked) => handleToggleUserBibleCurriculum(u.id, checked)}
                                  data-testid={`bible-toggle-user-${u.id}`}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Switch
                                  checked={u.christian_education_enabled || false}
                                  onCheckedChange={(checked) => handleToggleUserChristianEducation(u.id, checked)}
                                  data-testid={`christian-toggle-user-${u.id}`}
                                />
                              </TableCell>
                              <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                              <TableCell>
                                {u.last_login ? (
                                  <span className="text-sm" title={new Date(u.last_login).toLocaleString()}>
                                    {new Date(u.last_login).toLocaleDateString()}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 text-sm">Never</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    title="Impersonate User"
                                    onClick={() => handleImpersonateUser(u.id, u.name || u.email)}
                                    disabled={u.is_admin}
                                    data-testid={`impersonate-user-${u.id}`}
                                  >
                                    <UserX className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    title="Apply Promo Code"
                                    onClick={() => openApplyPromoDialog(u.id, u.email)}
                                    data-testid={`apply-promo-user-${u.id}`}
                                  >
                                    <Tag className="w-4 h-4 text-emerald-600" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600"
                                    onClick={() => window.open(`/admin/user/${u.id}`, '_blank')}
                                    title="View profile"
                                    data-testid={`view-profile-user-${u.id}`}
                                  >
                                    <User className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditUserDialog({ open: true, user: u })}>
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-red-500"
                                    onClick={() => setDeleteUserDialog({ open: true, userId: u.id, userName: u.name })}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Organization Users Sections */}
                {(userGroupFilter === 'all' || userGroupFilter === 'org') && groupedUsers.organizations?.map((org) => (
                  <Card key={org.organization_id}>
                    <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleOrgExpanded(org.organization_id)}>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          {expandedOrgs[org.organization_id] 
                            ? <ChevronDown className="w-5 h-5 text-purple-500" />
                            : <ChevronRight className="w-5 h-5 text-purple-500" />
                          }
                          <Building2 className="w-5 h-5 text-purple-500" />
                          {org.organization_name} ({org.users?.length || 0} users)
                        </CardTitle>
                        {expandedOrgs[org.organization_id] && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); toggleSelectAll(org.users); }}
                          >
                            {org.users.every(u => selectedUsers.includes(u.id)) ? 'Deselect All' : 'Select All'}
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    {expandedOrgs[org.organization_id] && (
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12"></TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Role</TableHead>
                              <TableHead>Subscription</TableHead>
                              <TableHead className="text-center">Christian Ed</TableHead>
                              <TableHead>Joined</TableHead>
                              <TableHead>Last Login</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {org.users?.filter(u => 
                              !userSearch || 
                              u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
                              u.email?.toLowerCase().includes(userSearch.toLowerCase())
                            ).map((u) => (
                              <TableRow key={u.id} className={selectedUsers.includes(u.id) ? "bg-purple-50" : ""}>
                                <TableCell>
                                  <Checkbox 
                                    checked={selectedUsers.includes(u.id)}
                                    onCheckedChange={() => toggleUserSelection(u.id)}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{u.name}</TableCell>
                                <TableCell>{u.email}</TableCell>
                                <TableCell>
                                  <span className={cn(
                                    "px-2 py-1 rounded-full text-xs capitalize",
                                    u.account_type === 'org_admin' ? 'bg-amber-100 text-amber-700' :
                                    u.account_type === 'teacher' ? 'bg-indigo-100 text-indigo-700' :
                                    'bg-slate-100 text-slate-700'
                                  )}>
                                    {u.account_type?.replace('_', ' ')}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    u.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-700' :
                                    u.subscription_plan === 'basic' ? 'bg-blue-100 text-blue-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {u.subscription_plan || 'free'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Switch
                                    checked={u.christian_education_enabled || false}
                                    onCheckedChange={(checked) => handleToggleUserChristianEducation(u.id, checked)}
                                    data-testid={`christian-toggle-org-user-${u.id}`}
                                  />
                                </TableCell>
                                <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                                <TableCell>
                                  {u.last_login ? (
                                    <span className="text-sm" title={new Date(u.last_login).toLocaleString()}>
                                      {new Date(u.last_login).toLocaleDateString()}
                                    </span>
                                  ) : (
                                    <span className="text-slate-400 text-sm">Never</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      title="Impersonate User"
                                      onClick={() => handleImpersonateUser(u.id, u.name || u.email)}
                                      disabled={u.is_admin}
                                    >
                                      <UserX className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      title="Apply Promo Code"
                                      onClick={() => openApplyPromoDialog(u.id, u.email)}
                                    >
                                      <Tag className="w-4 h-4 text-emerald-600" />
                                    </Button>
                                    <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600"
                                    onClick={() => window.open(`/admin/user/${u.id}`, '_blank')}
                                    title="View profile"
                                    data-testid={`view-profile-user-${u.id}`}
                                  >
                                    <User className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditUserDialog({ open: true, user: u })}>
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                    <Button size="sm" variant="ghost" className="text-red-500"
                                      onClick={() => setDeleteUserDialog({ open: true, userId: u.id, userName: u.name })}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              /* Flat List View */
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox 
                            checked={users.length > 0 && users.every(u => selectedUsers.includes(u.id))}
                            onCheckedChange={() => toggleSelectAll(users)}
                          />
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Account Type</TableHead>
                        <TableHead>Subscription</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.filter(u => 
                        u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
                        u.email?.toLowerCase().includes(userSearch.toLowerCase())
                      ).map((u) => (
                        <TableRow key={u.id} className={selectedUsers.includes(u.id) ? "bg-blue-50" : ""}>
                          <TableCell>
                            <Checkbox 
                              checked={selectedUsers.includes(u.id)}
                              onCheckedChange={() => toggleUserSelection(u.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            {u.organization_name ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                                {u.organization_name}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-sm">Personal</span>
                            )}
                          </TableCell>
                          <TableCell className="capitalize">{u.account_type?.replace('_', ' ')}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              u.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-700' :
                              u.subscription_plan === 'basic' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {u.subscription_plan || 'free'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                title="Impersonate User"
                                onClick={() => handleImpersonateUser(u.id, u.name || u.email)}
                                disabled={u.is_admin}
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                title="Apply Promo Code"
                                onClick={() => openApplyPromoDialog(u.id, u.email)}
                              >
                                <Tag className="w-4 h-4 text-emerald-600" />
                              </Button>
                              <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600"
                                    onClick={() => window.open(`/admin/user/${u.id}`, '_blank')}
                                    title="View profile"
                                    data-testid={`view-profile-user-${u.id}`}
                                  >
                                    <User className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditUserDialog({ open: true, user: u })}>
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                              <Button size="sm" variant="ghost" className="text-red-500"
                                onClick={() => setDeleteUserDialog({ open: true, userId: u.id, userName: u.name })}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Support Tickets Section */}
        {activeSection === 'support' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Support Tickets</h1>
              <div className="flex gap-2">
                <Select value={ticketFilter.status} onValueChange={(v) => setTicketFilter({ ...ticketFilter, status: v })}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchTickets}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{ticketStats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{ticketStats.open}</p>
                <p className="text-xs text-slate-500">Open</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{ticketStats.in_progress}</p>
                <p className="text-xs text-slate-500">In Progress</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{ticketStats.resolved}</p>
                <p className="text-xs text-slate-500">Resolved</p>
              </CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Subject</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{ticket.subject}</p>
                            <p className="text-xs text-slate-500 truncate max-w-xs">{ticket.description}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{ticket.user_name}</p>
                          <p className="text-xs text-slate-500">{ticket.user_email}</p>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            ticket.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                            ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {ticket.priority}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            ticket.status === 'open' ? 'bg-red-100 text-red-700' :
                            ticket.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {ticket.status?.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell>{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setSelectedTicket(ticket)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleUpdateTicket(ticket.id, { status: 'resolved' })}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* FAQ Section */}
        {activeSection === 'faq' && (
          <KBAdminSection api={api} />
        )}

        {/* Pricing Section */}
        {activeSection === 'pricing' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Pricing Plans</h1>
              <Button onClick={() => setCreatePlanDialog(true)} data-testid="create-plan-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Plan
              </Button>
            </div>
            
            {pricingPlans.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No pricing plans configured.</p>
                  <p className="text-sm">Create your first plan to get started.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pricingPlans.map((plan) => (
                  <Card key={plan.plan_id} className={`relative ${plan.is_active === false ? 'opacity-60' : ''}`} data-testid={`pricing-card-${plan.plan_id}`}>
                    {plan.is_active === false && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded-full">Inactive</span>
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {plan.name}
                            <span className="text-xs font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              {plan.plan_type || 'individual'}
                            </span>
                          </CardTitle>
                          <p className="text-xs text-slate-400 mt-1">ID: {plan.plan_id}</p>
                        </div>
                      </div>
                      <CardDescription className="text-3xl font-bold text-emerald-600 mt-2">
                        ${plan.price}<span className="text-sm font-normal text-slate-500">/month</span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Features</p>
                        <ul className="space-y-2">
                          {plan.features?.split(',').map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              <span>{feature.trim()}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex gap-2 pt-4 border-t">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => {
                            setEditingPlan({ ...plan });
                            setEditPlanDialog(true);
                          }}
                          data-testid={`edit-plan-${plan.plan_id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-2" /> Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          className="text-red-500"
                          onClick={() => handleDeletePlan(plan.plan_id)}
                          data-testid={`delete-plan-${plan.plan_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reports Section */}
        {activeSection === 'reports' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-slate-800">Reports & Analytics</h1>
            <Card>
              <CardContent className="p-12 text-center text-slate-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Advanced analytics coming soon</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Team Section */}
        {activeSection === 'team' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Team Management</h1>
              <Button onClick={() => setAddEmployeeDialog(true)}>
                <UserPlus className="w-4 h-4 mr-2" /> Add Employee
              </Button>
            </div>
            
            {/* Team Stats */}
            <div className="grid sm:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{teamStats.total || 0}</p>
                  <p className="text-xs text-slate-500">Total Employees</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{teamStats.active || 0}</p>
                  <p className="text-xs text-slate-500">Active</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{teamStats.by_department?.support || 0}</p>
                  <p className="text-xs text-slate-500">Support</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{teamStats.by_department?.engineering || 0}</p>
                  <p className="text-xs text-slate-500">Engineering</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-0">
                {teamLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <UsersRound className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No team members yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {teamMembers.map((emp) => (
                        <TableRow key={emp.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-sm">
                                {emp.name?.charAt(0).toUpperCase()}
                              </div>
                              {emp.name}
                            </div>
                          </TableCell>
                          <TableCell>{emp.email}</TableCell>
                          <TableCell>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium capitalize",
                              emp.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                              emp.role === 'developer' ? 'bg-blue-100 text-blue-700' :
                              emp.role === 'content_manager' ? 'bg-green-100 text-green-700' :
                              'bg-slate-100 text-slate-700'
                            )}>
                              {emp.role?.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell className="capitalize">{emp.department?.replace('_', ' ')}</TableCell>
                          <TableCell>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs",
                              emp.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            )}>
                              {emp.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {!emp.is_admin && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleImpersonateUser(emp.id, emp.name || emp.email)}
                                  title="Impersonate this employee"
                                  data-testid={`pa-impersonate-${emp.id}`}
                                >
                                  <UserCog className="w-4 h-4 text-indigo-600" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleUpdateEmployee(emp.id, { is_active: !emp.is_active })}
                                title={emp.is_active !== false ? 'Deactivate' : 'Activate'}
                              >
                                {emp.is_active !== false ? <UserCheck className="w-4 h-4 text-green-600" /> : <UserCheck className="w-4 h-4 text-slate-400" />}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-500"
                                onClick={() => handleDeleteEmployee(emp.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* License Requests Section */}
        {activeSection === 'license-requests' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">License Requests</h1>
              <Button variant="outline" onClick={fetchLicenseRequests}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
            
            {/* Request Stats */}
            <div className="grid sm:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{licenseRequestStats.total || 0}</p>
                  <p className="text-xs text-slate-500">Total Requests</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-600">{licenseRequestStats.pending || 0}</p>
                  <p className="text-xs text-slate-500">Pending</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{licenseRequestStats.approved || 0}</p>
                  <p className="text-xs text-slate-500">Approved</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{licenseRequestStats.denied || 0}</p>
                  <p className="text-xs text-slate-500">Denied</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-0">
                {licenseRequestsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : licenseRequests.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No license requests</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Organization</TableHead>
                        <TableHead>Requester</TableHead>
                        <TableHead>Licenses</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {licenseRequests.map((req) => (
                        <TableRow key={req.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{req.organization_name}</p>
                              <p className="text-xs text-slate-500">{req.organization_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm">{req.requester_name}</p>
                              <p className="text-xs text-slate-500">{req.requester_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="font-bold text-lg">{req.count}</TableCell>
                          <TableCell className="max-w-xs truncate">{req.reason || '-'}</TableCell>
                          <TableCell>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium capitalize",
                              req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              req.status === 'approved' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            )}>
                              {req.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {req.created_at ? new Date(req.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {req.status === 'pending' ? (
                              <div className="flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-green-600"
                                  onClick={() => setProcessRequestDialog({ open: true, request: req })}
                                >
                                  <CheckSquare className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-red-600"
                                  onClick={() => {
                                    setProcessRequestDialog({ open: true, request: req });
                                    setAdminNotes('');
                                  }}
                                >
                                  <XSquare className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">Processed</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* CRM Section */}
        {activeSection === 'crm' && (
          <CRMSection />
        )}

        {/* Leads Section */}
        {activeSection === 'leads' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Leads</h1>
              <Button onClick={() => toast.info('Create Lead feature coming soon')}>
                <Plus className="w-4 h-4 mr-2" /> Add Lead
              </Button>
            </div>
            
            <div className="grid sm:grid-cols-4 gap-4">
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{leadsStats.total || 0}</p><p className="text-xs text-slate-500">Total Leads</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-600">{leadsStats.new || 0}</p><p className="text-xs text-slate-500">New</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600">{leadsStats.quote_sent || 0}</p><p className="text-xs text-slate-500">Quote Sent</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600">{leadsStats.won || 0}</p><p className="text-xs text-slate-500">Won</p></CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-0">
                {leads.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">No leads yet</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Organization</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Est. Students</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((lead) => (
                        <TableRow key={lead.id}>
                          <TableCell className="font-medium">{lead.organization_name}</TableCell>
                          <TableCell>{lead.contact_name}</TableCell>
                          <TableCell>{lead.contact_email}</TableCell>
                          <TableCell>{lead.estimated_students}</TableCell>
                          <TableCell>
                            <span className={cn("px-2 py-1 rounded-full text-xs", 
                              lead.status === 'won' ? 'bg-emerald-100 text-emerald-700' :
                              lead.status === 'lost' ? 'bg-red-100 text-red-700' :
                              lead.status === 'quote_sent' ? 'bg-amber-100 text-amber-700' :
                              'bg-blue-100 text-blue-700'
                            )}>
                              {lead.status?.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell>{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quotes Section */}
        {activeSection === 'quotes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Quotes</h1>
              <Button onClick={() => setCreateQuoteDialog(true)} className="bg-emerald-500 hover:bg-emerald-600">
                <Plus className="w-4 h-4 mr-2" /> Generate Quote
              </Button>
            </div>
            
            <div className="grid sm:grid-cols-4 gap-4">
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{quotesStats.total || 0}</p><p className="text-xs text-slate-500">Total Quotes</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-slate-600">{quotesStats.draft || 0}</p><p className="text-xs text-slate-500">Draft</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-600">{quotesStats.sent || 0}</p><p className="text-xs text-slate-500">Sent</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600">{quotesStats.accepted || 0}</p><p className="text-xs text-slate-500">Accepted</p></CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-0">
                {quotes.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">No quotes yet. Click "Generate Quote" to create one.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Quote #</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Students</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Valid Until</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quotes.map((quote) => (
                        <TableRow key={quote.id}>
                          <TableCell className="font-mono text-sm">{quote.quote_number}</TableCell>
                          <TableCell className="font-medium">{quote.organization_name}</TableCell>
                          <TableCell>{quote.student_count}</TableCell>
                          <TableCell className="font-bold">${quote.total?.toLocaleString()}</TableCell>
                          <TableCell>
                            <span className={cn("px-2 py-1 rounded-full text-xs capitalize",
                              quote.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                              quote.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              quote.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            )}>
                              {quote.status}
                            </span>
                          </TableCell>
                          <TableCell>{quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {quote.status === 'draft' && (
                                <Button size="sm" variant="ghost" onClick={() => sendQuoteEmail(quote.id)}>
                                  <Send className="w-4 h-4" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => updateQuoteStatus(quote.id, 'accepted')}>
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Purchase Orders Section */}
        {activeSection === 'purchase-orders' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Purchase Orders</h1>
              <Button onClick={() => setCreatePODialog(true)} className="bg-purple-500 hover:bg-purple-600">
                <Plus className="w-4 h-4 mr-2" /> Create PO
              </Button>
            </div>
            
            <div className="grid sm:grid-cols-4 gap-4">
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{poStats.total || 0}</p><p className="text-xs text-slate-500">Total POs</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600">{poStats.sent || 0}</p><p className="text-xs text-slate-500">Sent</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600">{poStats.paid || 0}</p><p className="text-xs text-slate-500">Paid</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600">${(poStats.total_revenue || 0).toLocaleString()}</p><p className="text-xs text-slate-500">Revenue</p></CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-0">
                {purchaseOrders.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">No purchase orders yet. Create one for organizations that can't use Stripe.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>PO #</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Students</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Payment Terms</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {purchaseOrders.map((po) => (
                        <TableRow key={po.id}>
                          <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
                          <TableCell className="font-medium">{po.organization_name}</TableCell>
                          <TableCell>{po.student_count}</TableCell>
                          <TableCell className="font-bold">${po.total?.toLocaleString()}</TableCell>
                          <TableCell>{po.payment_terms}</TableCell>
                          <TableCell>
                            <span className={cn("px-2 py-1 rounded-full text-xs capitalize",
                              po.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                              po.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                              po.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-700'
                            )}>
                              {po.status}
                            </span>
                          </TableCell>
                          <TableCell>{po.due_date ? new Date(po.due_date).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => {
                                setEditPOData(po);
                                setEditPODialog(true);
                              }}>
                                <Edit2 className="w-4 h-4 text-slate-500" />
                              </Button>
                              {po.status !== 'paid' && (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => updatePOStatus(po.id, 'paid')}>
                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleDeletePO(po.id)} className="text-red-600 hover:text-red-700">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* Revenue Section */}
        {activeSection === 'revenue' && (
          <RevenueSection api={api} />
        )}


        {/* Promo Codes Section */}
        {activeSection === 'promo-codes' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <h1 className="text-2xl font-bold text-slate-800">Promo Codes</h1>
              <div className="flex gap-2 flex-wrap">
                {[
                  {
                    key: 'family3',
                    label: 'FAMILY3',
                    tip: 'FAMILY3 — 25% off for 3 months, first-time subscribers only',
                    values: {
                      code: 'FAMILY3', discount_type: 'percentage', discount_value: 25,
                      expiration_date: '', max_uses: '', first_time_only: true,
                      applicable_plans: [],
                      description: '25% off for 3 months — new family subscribers',
                      duration: 'repeating', duration_in_months: 3,
                    },
                  },
                  {
                    key: 'holiday50',
                    label: 'HOLIDAY-50',
                    tip: 'HOLIDAY-50 — 50% off 1 month, first-time subscribers only',
                    values: {
                      code: 'HOLIDAY50', discount_type: 'percentage', discount_value: 50,
                      expiration_date: '', max_uses: '', first_time_only: true,
                      applicable_plans: [],
                      description: '50% off first month — holiday promo',
                      duration: 'once', duration_in_months: 3,
                    },
                  },
                  {
                    key: 'sponsor-forever',
                    label: 'SPONSOR-FOREVER',
                    tip: 'SPONSOR-FOREVER — 10% off forever for referred subscribers',
                    values: {
                      code: 'SPONSORFOREVER', discount_type: 'percentage', discount_value: 10,
                      expiration_date: '', max_uses: '', first_time_only: false,
                      applicable_plans: [],
                      description: '10% off forever — referred subscribers',
                      duration: 'forever', duration_in_months: 3,
                    },
                  },
                ].map((preset) => (
                  <Button
                    key={preset.key}
                    variant="outline"
                    onClick={() => { setNewPromo(preset.values); setCreatePromoDialog(true); }}
                    data-testid={`promo-preset-${preset.key}-btn`}
                    title={preset.tip}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Preset: {preset.label}
                  </Button>
                ))}
                <Button onClick={() => setCreatePromoDialog(true)} data-testid="create-promo-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Promo Code
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-6">
                {promoLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : promoCodes.length === 0 ? (
                  <div className="text-center py-12">
                    <Tag className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No promo codes yet</p>
                    <Button className="mt-4" onClick={() => setCreatePromoDialog(true)}>
                      Create First Promo Code
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Discount</TableHead>
                        <TableHead>Usage</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {promoCodes.map((promo) => (
                        <TableRow key={promo.code}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-emerald-600">{promo.code}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                  navigator.clipboard.writeText(promo.code);
                                  toast.success('Code copied!');
                                }}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                            {promo.description && (
                              <p className="text-xs text-slate-500 mt-1">{promo.description}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-sm font-medium",
                              promo.discount_type === 'percentage' ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                            )}>
                              {promo.discount_type === 'percentage' ? (
                                <span className="flex items-center gap-1">
                                  <Percent className="w-3 h-3" />
                                  {promo.discount_value}% off
                                </span>
                              ) : (
                                `$${promo.discount_value} off`
                              )}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {promo.current_uses || 0}
                              {promo.max_uses && ` / ${promo.max_uses}`}
                            </span>
                            {promo.first_time_only && (
                              <span className="block text-xs text-amber-600">First-time only</span>
                            )}
                            {promo.duration === 'repeating' && promo.duration_in_months ? (
                              <span className="block text-xs text-emerald-700">
                                {promo.duration_in_months} month{promo.duration_in_months > 1 ? 's' : ''} recurring
                              </span>
                            ) : promo.duration === 'forever' ? (
                              <span className="block text-xs text-emerald-700">Forever</span>
                            ) : (
                              <span className="block text-xs text-slate-400">1 month</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {promo.expiration_date ? (
                              <span className="text-sm">
                                {new Date(promo.expiration_date).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">No expiry</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              promo.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                            )}>
                              {promo.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Copy shareable link"
                                onClick={() => {
                                  const url = `${window.location.origin}/pricing?code=${encodeURIComponent(promo.code)}`;
                                  navigator.clipboard.writeText(url).then(
                                    () => toast.success(`Link copied — ${url}`),
                                    () => toast.error('Clipboard blocked; copy manually: ' + url)
                                  );
                                }}
                                data-testid={`promo-share-${promo.code}`}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setEditingPromo({...promo});
                                  setEditPromoDialog(true);
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeletePromo(promo.code)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Create Promo Dialog */}
            <Dialog open={createPromoDialog} onOpenChange={setCreatePromoDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create Promo Code</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Code</Label>
                    <Input
                      value={newPromo.code}
                      onChange={(e) => setNewPromo({...newPromo, code: e.target.value.toUpperCase()})}
                      placeholder="e.g., SAVE20"
                      className="uppercase"
                      data-testid="promo-code-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Discount Type</Label>
                      <Select
                        value={newPromo.discount_type}
                        onValueChange={(v) => setNewPromo({...newPromo, discount_type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Discount Value</Label>
                      <Input
                        type="number"
                        value={newPromo.discount_value}
                        onChange={(e) => setNewPromo({...newPromo, discount_value: parseFloat(e.target.value) || 0})}
                        placeholder={newPromo.discount_type === 'percentage' ? '20' : '5.00'}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Expiration Date (Optional)</Label>
                      <Input
                        type="date"
                        value={newPromo.expiration_date}
                        onChange={(e) => setNewPromo({...newPromo, expiration_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Max Uses (Optional)</Label>
                      <Input
                        type="number"
                        value={newPromo.max_uses}
                        onChange={(e) => setNewPromo({...newPromo, max_uses: e.target.value})}
                        placeholder="Unlimited"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newPromo.first_time_only}
                      onCheckedChange={(v) => setNewPromo({...newPromo, first_time_only: v})}
                    />
                    <Label>First-time subscribers only</Label>
                  </div>

                  {/* Duration — how many billing cycles the discount applies to */}
                  <div className="grid grid-cols-2 gap-4 bg-emerald-50/60 border border-emerald-200 rounded-lg p-3">
                    <div>
                      <Label>Applies for</Label>
                      <Select
                        value={newPromo.duration}
                        onValueChange={(v) => setNewPromo({...newPromo, duration: v})}
                      >
                        <SelectTrigger data-testid="promo-duration-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="once">1 month (single invoice)</SelectItem>
                          <SelectItem value="repeating">Multiple months</SelectItem>
                          <SelectItem value="forever">Forever (every renewal)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        {newPromo.duration === 'once' && 'Discount applied to the first month only.'}
                        {newPromo.duration === 'repeating' && 'Discount repeats for the number of months you choose.'}
                        {newPromo.duration === 'forever' && 'Discount stays as long as the subscription is active.'}
                      </p>
                    </div>
                    <div>
                      <Label>Number of months</Label>
                      <Input
                        type="number"
                        min={1}
                        max={60}
                        value={newPromo.duration_in_months}
                        onChange={(e) => setNewPromo({...newPromo, duration_in_months: parseInt(e.target.value) || 1})}
                        placeholder="e.g. 3"
                        disabled={newPromo.duration !== 'repeating'}
                        data-testid="promo-duration-months-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Only used when "Applies for" is set to Multiple months.
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label>Description (Optional)</Label>
                    <Input
                      value={newPromo.description}
                      onChange={(e) => setNewPromo({...newPromo, description: e.target.value})}
                      placeholder="e.g., Summer sale discount"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreatePromoDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreatePromo} disabled={!newPromo.code || !newPromo.discount_value}>
                    Create Promo Code
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Promo Dialog */}
            <Dialog open={editPromoDialog} onOpenChange={setEditPromoDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit Promo Code: {editingPromo?.code}</DialogTitle>
                </DialogHeader>
                {editingPromo && (
                  <div className="space-y-4">
                    {/* Immutable-coupon warning — Stripe locks these fields once a coupon exists. */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="edit-promo-stripe-note">
                      <p className="font-semibold flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4" /> Some fields cannot be changed
                      </p>
                      <p className="text-xs text-amber-700 leading-relaxed">
                        Stripe locks a coupon's <strong>discount type</strong>, <strong>discount value</strong>, and <strong>duration</strong> the moment it's created. Editing them here updates the local database only — new checkouts will keep using the original Stripe coupon rules. To change any of these, <strong>delete this promo and create a new one</strong>.
                      </p>
                      <p className="text-xs text-amber-700 mt-2">
                        Safe to change: expiry date, max uses, first-time-only, applicable plans, description, and active/inactive.
                      </p>
                    </div>

                    {editingPromo.duration && (
                      <div className="text-xs text-slate-500">
                        Current Stripe duration: <strong>
                          {editingPromo.duration === 'repeating'
                            ? `${editingPromo.duration_in_months || '?'} months recurring`
                            : editingPromo.duration === 'forever'
                              ? 'Forever'
                              : '1 month'}
                        </strong>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Discount Type</Label>
                        <Select
                          value={editingPromo.discount_type}
                          onValueChange={(v) => setEditingPromo({...editingPromo, discount_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage (%)</SelectItem>
                            <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Discount Value</Label>
                        <Input
                          type="number"
                          value={editingPromo.discount_value}
                          onChange={(e) => setEditingPromo({...editingPromo, discount_value: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Expiration Date</Label>
                        <Input
                          type="date"
                          value={editingPromo.expiration_date?.split('T')[0] || ''}
                          onChange={(e) => setEditingPromo({...editingPromo, expiration_date: e.target.value || null})}
                        />
                      </div>
                      <div>
                        <Label>Max Uses</Label>
                        <Input
                          type="number"
                          value={editingPromo.max_uses || ''}
                          onChange={(e) => setEditingPromo({...editingPromo, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editingPromo.first_time_only}
                        onCheckedChange={(v) => setEditingPromo({...editingPromo, first_time_only: v})}
                      />
                      <Label>First-time subscribers only</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editingPromo.is_active}
                        onCheckedChange={(v) => setEditingPromo({...editingPromo, is_active: v})}
                      />
                      <Label>Active</Label>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={editingPromo.description || ''}
                        onChange={(e) => setEditingPromo({...editingPromo, description: e.target.value})}
                      />
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditPromoDialog(false)}>Cancel</Button>
                  <Button onClick={handleUpdatePromo}>Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Announcements Section */}
        {activeSection === 'announcements' && (
          <AnnouncementsSection api={api} />
        )}


        {/* Referral Program Section */}
        {activeSection === 'referrals' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Referral Program</h1>
                <p className="text-slate-600">Manage referral links, rewards, and program settings</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setCreateReferralDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="create-referral-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Referral Link
                </Button>
                <Button 
                  onClick={() => setEditReferralSettingsDialog(true)}
                  variant="outline"
                  data-testid="edit-referral-settings"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Program Settings
                </Button>
              </div>
            </div>

            {/* Referral Stats */}
            {referralStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-700">{referralStats.total_referral_links}</p>
                    <p className="text-xs text-blue-600">Total Referral Links</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-700">{referralStats.active_referrers}</p>
                    <p className="text-xs text-emerald-600">Active Referrers</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-amber-700">{referralStats.total_referred_users}</p>
                    <p className="text-xs text-amber-600">Users Referred</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-purple-700">{referralStats.total_active_reward_months}</p>
                    <p className="text-xs text-purple-600">Active Reward Months</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-pink-700">${referralStats.estimated_savings_given?.toFixed(0) || 0}</p>
                    <p className="text-xs text-pink-600">Est. Savings Given</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Program Settings Card */}
            {referralSettings && (
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
                <CardHeader>
                  <CardTitle className="text-amber-800 flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Current Program Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white rounded-lg p-3 border border-amber-200">
                      <p className="text-xs text-amber-600 mb-1">New User Discount</p>
                      <p className="text-2xl font-bold text-amber-800">{referralSettings.new_user_discount_percent}%</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-amber-200">
                      <p className="text-xs text-amber-600 mb-1">Referrer Discount</p>
                      <p className="text-2xl font-bold text-amber-800">{referralSettings.referrer_discount_percent}%</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-amber-200">
                      <p className="text-xs text-amber-600 mb-1">Reward Months</p>
                      <p className="text-2xl font-bold text-amber-800">{referralSettings.referrer_reward_months}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-amber-200">
                      <p className="text-xs text-amber-600 mb-1">Max Stacked Rewards</p>
                      <p className="text-2xl font-bold text-amber-800">{referralSettings.max_active_rewards}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-amber-200">
                      <p className="text-xs text-amber-600 mb-1">Program Status</p>
                      <Badge className={referralSettings.is_program_active ? "bg-green-500" : "bg-red-500"}>
                        {referralSettings.is_program_active ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Referrers */}
            {referralStats?.top_referrers?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Top Referrers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {referralStats.top_referrers.map((referrer, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center font-bold text-white",
                            index === 0 ? "bg-amber-500" : index === 1 ? "bg-slate-400" : index === 2 ? "bg-amber-700" : "bg-slate-300"
                          )}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{referrer.user_name}</p>
                            <p className="text-xs text-slate-500">{referrer.user_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">{referrer.total_referrals} referrals</p>
                          <code className="text-xs bg-slate-200 px-2 py-0.5 rounded">{referrer.referral_code}</code>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Referrals Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Referral Links</CardTitle>
              </CardHeader>
              <CardContent>
                {referralsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : referrals.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No referral links created yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 text-sm font-medium text-slate-600">User</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Code</th>
                          <th className="text-center p-3 text-sm font-medium text-slate-600">Referrals</th>
                          <th className="text-center p-3 text-sm font-medium text-slate-600">Active Rewards</th>
                          <th className="text-center p-3 text-sm font-medium text-slate-600">Status</th>
                          <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((ref) => (
                          <tr key={ref.id} className="border-b hover:bg-slate-50">
                            <td className="p-3">
                              <p className="font-medium text-slate-800">{ref.user_name}</p>
                              <p className="text-xs text-slate-500">{ref.user_email}</p>
                            </td>
                            <td className="p-3">
                              <code className="bg-amber-100 px-2 py-1 rounded text-amber-800 font-mono text-sm">
                                {ref.referral_code}
                              </code>
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-bold text-emerald-600">{ref.total_referrals}</span>
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-bold text-purple-600">{ref.active_reward_months || 0}</span>
                            </td>
                            <td className="p-3 text-center">
                              <Badge className={ref.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                                {ref.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(ref.referral_link);
                                    toast.success('Link copied!');
                                  }}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleToggleReferral(ref.id, ref.is_active)}
                                >
                                  {ref.is_active ? <XCircle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-green-500" />}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Referrals */}
            {referralStats?.recent_referrals?.length > 0 && (
              <Card className="border-2 border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-emerald-700 flex items-center gap-2">
                    <UserPlus className="w-5 h-5" />
                    Recent Referrals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {referralStats.recent_referrals.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <UserPlus className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{item.new_user?.name || 'New User'}</p>
                            <p className="text-xs text-slate-500">{item.new_user?.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-emerald-600">Referred by {item.referrer_name}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(item.new_user?.signed_up_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Edit Settings Dialog */}
            <Dialog open={editReferralSettingsDialog} onOpenChange={setEditReferralSettingsDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Referral Program Settings</DialogTitle>
                  <DialogDescription>Configure discounts and rewards for your referral program</DialogDescription>
                </DialogHeader>
                {referralSettings && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>New User Discount (%)</Label>
                        <Input 
                          type="number" 
                          value={referralSettings.new_user_discount_percent}
                          onChange={(e) => setReferralSettings({...referralSettings, new_user_discount_percent: parseInt(e.target.value)})}
                        />
                        <p className="text-xs text-slate-500 mt-1">Discount for new subscribers using a referral link</p>
                      </div>
                      <div>
                        <Label>Referrer Discount (%)</Label>
                        <Input 
                          type="number" 
                          value={referralSettings.referrer_discount_percent}
                          onChange={(e) => setReferralSettings({...referralSettings, referrer_discount_percent: parseInt(e.target.value)})}
                        />
                        <p className="text-xs text-slate-500 mt-1">Monthly discount for the person who referred</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Reward Months</Label>
                        <Input 
                          type="number" 
                          value={referralSettings.referrer_reward_months}
                          onChange={(e) => setReferralSettings({...referralSettings, referrer_reward_months: parseInt(e.target.value)})}
                        />
                        <p className="text-xs text-slate-500 mt-1">Number of months the referrer gets discount</p>
                      </div>
                      <div>
                        <Label>Max Stacked Rewards</Label>
                        <Input 
                          type="number" 
                          value={referralSettings.max_active_rewards}
                          onChange={(e) => setReferralSettings({...referralSettings, max_active_rewards: parseInt(e.target.value)})}
                        />
                        <p className="text-xs text-slate-500 mt-1">Maximum months of rewards that can stack</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">Program Active</p>
                        <p className="text-sm text-slate-500">Enable or disable the referral program</p>
                      </div>
                      <Switch 
                        checked={referralSettings.is_program_active}
                        onCheckedChange={(checked) => setReferralSettings({...referralSettings, is_program_active: checked})}
                      />
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditReferralSettingsDialog(false)}>Cancel</Button>
                  <Button onClick={handleUpdateReferralSettings}>Save Settings</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Create Referral for User Dialog */}
            <Dialog open={createReferralDialog} onOpenChange={setCreateReferralDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Referral Link for User</DialogTitle>
                  <DialogDescription>Generate a referral code and link for an existing user</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Select User</Label>
                    <Select value={selectedUserForReferral} onValueChange={setSelectedUserForReferral}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateReferralDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateReferralForUser} disabled={creatingReferral || !selectedUserForReferral}>
                    {creatingReferral ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />}
                    Generate Referral Link
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Inactivity Reminders Section */}
            <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-orange-700 flex items-center gap-2">
                      <Bell className="w-5 h-5" />
                      Inactivity Reminders
                    </CardTitle>
                    <CardDescription>Send reminders to parents of inactive children</CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-orange-700">Days inactive:</Label>
                      <Select value={String(reminderDaysThreshold)} onValueChange={(v) => setReminderDaysThreshold(Number(v))}>
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                          <SelectItem value="3">3</SelectItem>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="7">7</SelectItem>
                          <SelectItem value="14">14</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={fetchInactiveChildren}
                      disabled={inactiveLoading}
                    >
                      <RefreshCw className={cn("w-4 h-4 mr-2", inactiveLoading && "animate-spin")} />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {inactiveLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                  </div>
                ) : inactiveChildren.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-2" />
                    <p className="text-green-700 font-medium">All children are active!</p>
                    <p className="text-sm text-slate-500">No children have been inactive for {reminderDaysThreshold}+ days</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-orange-700 font-medium">{inactiveChildren.length} children inactive for {reminderDaysThreshold}+ days</p>
                      <Button 
                        onClick={() => handleSendReminder(null, true)} 
                        disabled={sendingReminder}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        {sendingReminder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Send All Reminders
                      </Button>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {inactiveChildren.map((child) => (
                        <div key={child.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                              <span className="text-orange-600 font-semibold">{child.name?.charAt(0)?.toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{child.name}</p>
                              <p className="text-xs text-slate-500">
                                Parent: {child.parent_email || 'Unknown'}
                                {child.days_inactive && ` • ${child.days_inactive} days inactive`}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleSendReminder(child.id)}
                            disabled={sendingReminder}
                            className="border-orange-300 text-orange-700"
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Send
                          </Button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      ⏰ Automatic reminders are sent daily at 10:00 AM UTC for children inactive 3+ days (max once per 7 days per child)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* QR Code Tracking Section */}
            <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-blue-700 flex items-center gap-2">
                      <QrCode className="w-5 h-5" />
                      QR Code Campaigns
                    </CardTitle>
                    <CardDescription>Track QR code scans from flyers and marketing materials</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchQrCampaigns} disabled={qrLoading}>
                    <RefreshCw className={cn("w-4 h-4 mr-2", qrLoading && "animate-spin")} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {qrLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                  </div>
                ) : qrCampaigns.length === 0 ? (
                  <div className="text-center py-8">
                    <QrCode className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No QR campaigns created yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {qrCampaigns.map((campaign) => (
                      <div key={campaign.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-200">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-white rounded-lg border border-blue-200 flex items-center justify-center">
                            <img 
                              src={`/api/qr-image/${campaign.id}`} 
                              alt="QR Code" 
                              className="w-14 h-14"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{campaign.name}</p>
                            <p className="text-xs text-slate-500">{campaign.description}</p>
                            <p className="text-xs text-blue-600 mt-1">
                              Code: <code className="bg-blue-100 px-1 rounded">{campaign.promo_code || 'N/A'}</code>
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-blue-600">{campaign.scan_count || 0}</p>
                          <p className="text-xs text-slate-500">Total Scans</p>
                          {campaign.last_scan && (
                            <p className="text-xs text-slate-400 mt-1">
                              Last: {new Date(campaign.last_scan).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <strong>Flyer Links:</strong><br/>
                    English: <code className="bg-white px-1 rounded">zazued.com/flyer-christian.html</code><br/>
                    Spanish: <code className="bg-white px-1 rounded">zazued.com/flyer-christian-es.html</code>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Newsletters Section */}
        {activeSection === 'newsletters' && (
          <NewsletterSection api={api} />
        )}

        {/* Settings Section */}
        {activeSection === 'settings' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-slate-800">Platform Settings</h1>

            {/* Zippie AI provider */}
            <ZippieSettingsCard />

            {/* Settings Tabs */}
            <div className="flex gap-2 border-b border-slate-200 pb-2">
              {['status', 'audit', 'emails', 'verification', 'api'].map((tab) => (
                <Button
                  key={tab}
                  variant={settingsTab === tab ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSettingsTab(tab)}
                  data-testid={`settings-tab-${tab}`}
                >
                  {tab === 'status' && 'System Status'}
                  {tab === 'audit' && 'Audit Logs'}
                  {tab === 'emails' && 'Email Logs'}
                  {tab === 'verification' && 'Resend Verification'}
                  {tab === 'api' && 'API Settings'}
                </Button>
              ))}
            </div>

            {/* System Status Tab */}
            {settingsTab === 'status' && (
              <div className="space-y-6">
                {settingsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : systemStatus ? (
                  <>
                    <div className="grid sm:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">Database</span>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              systemStatus.services.database === 'healthy' ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            )}>
                              {systemStatus.services.database}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">Email (Resend)</span>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              systemStatus.services.email === 'configured' ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                            )}>
                              {systemStatus.services.email}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">Payments (Stripe)</span>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              systemStatus.services.payments === 'configured' ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                            )}>
                              {systemStatus.services.payments}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">Google OAuth</span>
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              systemStatus.services.google_oauth === 'configured' ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                            )}>
                              {systemStatus.services.google_oauth}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Card>
                      <CardHeader>
                        <CardTitle>Platform Statistics</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid sm:grid-cols-4 gap-6">
                          <div>
                            <p className="text-3xl font-bold text-slate-900">{systemStatus.stats.total_users}</p>
                            <p className="text-sm text-slate-500">Total Users</p>
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-slate-900">{systemStatus.stats.total_organizations}</p>
                            <p className="text-sm text-slate-500">Organizations</p>
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-slate-900">{systemStatus.stats.total_students}</p>
                            <p className="text-sm text-slate-500">Students</p>
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-emerald-600">{systemStatus.stats.signups_last_7_days}</p>
                            <p className="text-sm text-slate-500">New Signups (7 days)</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Environment Configuration</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b">
                            <span className="text-sm text-slate-600">Frontend URL</span>
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded">{systemStatus.environment.frontend_url}</code>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b">
                            <span className="text-sm text-slate-600">Google Redirect URI</span>
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded">{systemStatus.environment.google_redirect_uri}</code>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b">
                            <span className="text-sm text-slate-600">Sender Email</span>
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded">{systemStatus.environment.sender_email}</code>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <span className="text-sm text-slate-600">Version</span>
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded">{systemStatus.version}</code>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Database Backup Card */}
                    <Card className="border-blue-200 bg-blue-50/30" data-testid="database-backup-card">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Download className="w-5 h-5 text-blue-600" />
                          Database Backup
                        </CardTitle>
                        <p className="text-sm text-slate-500">Download a full backup of all platform and organizational data as a ZIP archive</p>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-slate-600">
                            <p>Includes: Users, organizations, children, progress, payments, worksheets, songs, badges, and all other collections</p>
                            <p className="text-xs text-slate-400 mt-1">Sensitive fields (password hashes) are excluded for security</p>
                          </div>
                          <Button
                            data-testid="backup-now-btn"
                            className="bg-blue-600 hover:bg-blue-700 ml-4 shrink-0"
                            onClick={async () => {
                              const btn = document.querySelector('[data-testid="backup-now-btn"]');
                              if (btn) btn.disabled = true;
                              try {
                                toast.info('Starting full backup... this may take a moment');
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/full`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                });
                                if (!response.ok) throw new Error('Backup failed');
                                const blob = await response.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `zazu_backup_${new Date().toISOString().slice(0,10)}.zip`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                URL.revokeObjectURL(url);
                                toast.success('Backup downloaded successfully!');
                              } catch (err) {
                                toast.error('Backup failed: ' + err.message);
                              } finally {
                                if (btn) btn.disabled = false;
                              }
                            }}
                          >
                            <Download className="w-4 h-4 mr-2" /> Backup Now
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center text-slate-500">
                      <p>Failed to load system status</p>
                      <Button variant="outline" className="mt-4" onClick={fetchSystemStatus}>Retry</Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Audit Logs Tab */}
            {settingsTab === 'audit' && (
              <Card>
                <CardHeader>
                  <CardTitle>Audit Logs ({auditLogsTotal})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {auditLogs.length === 0 ? (
                    <div className="p-12 text-center text-slate-500">
                      <p>No audit logs found</p>
                    </div>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Timestamp</TableHead>
                            <TableHead>Action</TableHead>
                            <TableHead>Target</TableHead>
                            <TableHead>Admin</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditLogs.map((log, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-xs text-slate-500">
                                {new Date(log.timestamp).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <span className="px-2 py-1 bg-slate-100 rounded text-xs font-mono">
                                  {log.action}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm">
                                {log.target_email || log.target_user_id || log.organization_id || '-'}
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">
                                {log.admin_id || 'System'}
                              </TableCell>
                              <TableCell>
                                {log.success !== undefined && (
                                  <span className={cn(
                                    "px-2 py-1 rounded-full text-xs",
                                    log.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                                  )}>
                                    {log.success ? 'Success' : 'Failed'}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <div className="p-4 flex justify-between items-center border-t">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={auditLogsPage === 1}
                          onClick={() => fetchAuditLogs(auditLogsPage - 1)}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-slate-500">Page {auditLogsPage}</span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={auditLogs.length < 20}
                          onClick={() => fetchAuditLogs(auditLogsPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Email Logs Tab */}
            {settingsTab === 'emails' && (
              <Card>
                <CardHeader>
                  <CardTitle>Email Logs ({emailLogsTotal})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {emailLogs.length === 0 ? (
                    <div className="p-12 text-center text-slate-500">
                      <p>No email logs found</p>
                    </div>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Sent At</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>To</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Sent By</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {emailLogs.map((log, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-xs text-slate-500">
                                {new Date(log.sent_at).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                  {log.type}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm">{log.to_email}</TableCell>
                              <TableCell>
                                <span className={cn(
                                  "px-2 py-1 rounded-full text-xs",
                                  log.status === 'sent' ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                                )}>
                                  {log.status}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">{log.sent_by || 'System'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <div className="p-4 flex justify-between items-center border-t">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={emailLogsPage === 1}
                          onClick={() => fetchEmailLogs(emailLogsPage - 1)}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-slate-500">Page {emailLogsPage}</span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={emailLogs.length < 20}
                          onClick={() => fetchEmailLogs(emailLogsPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Resend Verification Tab */}
            {settingsTab === 'verification' && (
              <Card>
                <CardHeader>
                  <CardTitle>Resend Verification Email</CardTitle>
                  <p className="text-sm text-slate-500">Send verification emails to users who haven't verified their email</p>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.filter(u => !u.email_verified).slice(0, 20).map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                              Unverified
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResendVerification(u.id, u.name)}
                              disabled={resendingVerification === u.id}
                              data-testid={`resend-verification-${u.id}`}
                            >
                              {resendingVerification === u.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Mail className="w-4 h-4 mr-1" /> Resend
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {users.filter(u => !u.email_verified).length === 0 && (
                    <div className="p-12 text-center text-slate-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                      <p>All users have verified their emails!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* API Settings Tab */}
            {settingsTab === 'api' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>API Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-medium mb-2">Resend (Email Service)</h4>
                        <p className="text-sm text-slate-600 mb-2">Status: {systemStatus?.services?.email || 'Loading...'}</p>
                        <p className="text-xs text-slate-500">To enable full email functionality, verify your domain at resend.com/domains</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-medium mb-2">Stripe (Payments)</h4>
                        <p className="text-sm text-slate-600 mb-2">Status: {systemStatus?.services?.payments || 'Loading...'}</p>
                        <p className="text-xs text-slate-500">Payments are processed through Stripe</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-medium mb-2">Google OAuth</h4>
                        <p className="text-sm text-slate-600 mb-2">Status: {systemStatus?.services?.google_oauth || 'Loading...'}</p>
                        <p className="text-xs text-slate-500">
                          Redirect URI: <code className="bg-white px-1 py-0.5 rounded text-xs">{systemStatus?.environment?.google_redirect_uri}</code>
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-medium mb-2">OpenAI (AI Content)</h4>
                        <p className="text-sm text-slate-600 mb-2">Used for adaptive learning and content generation</p>
                        <p className="text-xs text-slate-500">Powered by Emergent LLM integration</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Webhook Endpoints</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-sm">Stripe Webhook</span>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">/api/webhooks/stripe</code>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm">Google Classroom Sync</span>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">/api/auth/google/classroom/callback</code>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Scheduled Reports Section */}
        {activeSection === 'scheduled-reports' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-800">Scheduled Reports</h1>
              <Button onClick={() => setCreateReportDialog(true)} data-testid="create-report-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Report
              </Button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{scheduledReports.length}</p>
                  <p className="text-xs text-slate-500">Total Reports</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{scheduledReports.filter(r => r.is_active).length}</p>
                  <p className="text-xs text-slate-500">Active</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-400">{scheduledReports.filter(r => !r.is_active).length}</p>
                  <p className="text-xs text-slate-500">Paused</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-0">
                {scheduledReports.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No scheduled reports yet.</p>
                    <p className="text-sm">Create one to automatically send reports to specified emails.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Report Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Recipients</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Sent</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduledReports.map((report) => {
                        const orgName = report.filters?.organization_id 
                          ? organizations.find(o => o.id === report.filters.organization_id)?.name || 'Unknown'
                          : 'All';
                        return (
                        <TableRow key={report.id} data-testid={`report-row-${report.id}`}>
                          <TableCell className="font-medium">{report.name}</TableCell>
                          <TableCell>
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 capitalize">
                              {report.report_type?.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">{orgName}</span>
                          </TableCell>
                          <TableCell className="capitalize">{report.frequency}</TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">
                              {report.recipients?.length || 0} recipient(s)
                            </span>
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={report.is_active}
                              onCheckedChange={async (checked) => {
                                try {
                                  await api.put(`/admin/scheduled-reports/${report.id}`, { is_active: checked });
                                  toast.success(`Report ${checked ? 'activated' : 'paused'}`);
                                  fetchScheduledReports();
                                } catch (error) {
                                  toast.error('Failed to update report status');
                                }
                              }}
                              data-testid={`report-toggle-${report.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {report.last_sent ? new Date(report.last_sent).toLocaleDateString() : 'Never'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => sendReportNow(report.id)}
                                title="Send Now"
                                data-testid={`send-report-${report.id}`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500"
                                onClick={async () => {
                                  if (window.confirm('Delete this scheduled report?')) {
                                    try {
                                      await api.delete(`/admin/scheduled-reports/${report.id}`);
                                      toast.success('Report deleted');
                                      fetchScheduledReports();
                                    } catch (error) {
                                      toast.error('Failed to delete report');
                                    }
                                  }
                                }}
                                data-testid={`delete-report-${report.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* Christian Education Section */}
        {activeSection === 'christian' && (
          <ChristianAdminSection 
            christianStats={christianStats}
            christianUsers={christianUsers}
            christianChildren={christianChildren}
            christianLoading={christianLoading}
            fetchChristianStats={fetchChristianStats}
            api={api}
          />
        )}

        {/* Subscription Billing Section */}
        {activeSection === 'billing' && (
          <PlatformAdminBilling />
        )}

        {/* Themes / Branding Section */}
        {activeSection === 'themes' && (
          <PlatformAdminThemes />
        )}


        {/* Tier Features Section */}
        {activeSection === 'tier-features' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Pricing Tier Features</h1>
                <p className="text-slate-500 text-sm mt-1">Configure features available in each subscription tier</p>
              </div>
              <Button variant="outline" onClick={fetchTierFeatures}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tierFeatures.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="p-12 text-center text-slate-500">
                    <Sliders className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No tier configurations found.</p>
                  </CardContent>
                </Card>
              ) : (
                tierFeatures.map((tier) => (
                  <Card key={tier.tier_id} className={cn(
                    "border-2 transition-all hover:shadow-lg",
                    tier.tier_id === 'free' && "border-slate-200",
                    tier.tier_id === 'basic' && "border-blue-200",
                    tier.tier_id === 'premium' && "border-purple-200",
                    tier.tier_id === 'enterprise' && "border-amber-200"
                  )} data-testid={`tier-card-${tier.tier_id}`}>
                    <CardHeader className="pb-3">
                      <CardTitle className={cn(
                        "flex items-center gap-2",
                        tier.tier_id === 'free' && "text-slate-700",
                        tier.tier_id === 'basic' && "text-blue-700",
                        tier.tier_id === 'premium' && "text-purple-700",
                        tier.tier_id === 'enterprise' && "text-amber-700"
                      )}>
                        <Crown className="w-5 h-5" />
                        <span className="capitalize">{tier.tier_id}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Max Children</span>
                          <span className="font-semibold">{tier.max_children === -1 ? 'Unlimited' : tier.max_children}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Daily Activities</span>
                          <span className="font-semibold">{tier.daily_activity_limit === -1 ? 'Unlimited' : tier.daily_activity_limit}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Progress Reports</span>
                          <span className={tier.progress_reports ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                            {tier.progress_reports ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Bible Curriculum</span>
                          <span className={tier.bible_curriculum ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                            {tier.bible_curriculum ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Priority Support</span>
                          <span className={tier.priority_support ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                            {tier.priority_support ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                      <Button
                        className="w-full mt-4"
                        variant="outline"
                        onClick={() => {
                          setEditingTier({ ...tier });
                          setEditTierDialog(true);
                        }}
                        data-testid={`edit-tier-${tier.tier_id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-2" /> Edit Features
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Flyer Generator Section */}
        {activeSection === 'flyer-generator' && (
          <FlyerGeneratorSection api={api} />
        )}
      </main>
      <Dialog open={createOrgDialog} onOpenChange={setCreateOrgDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Organization Details */}
            <div className="border-b pb-4">
              <h4 className="font-medium text-slate-700 mb-3">Organization Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Organization Name *</Label>
                  <Input
                    value={newOrg.name}
                    onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                    className="mt-1"
                    placeholder="e.g., Springfield Elementary"
                    data-testid="create-org-name"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={newOrg.organization_type} onValueChange={(v) => setNewOrg({ ...newOrg, organization_type: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="school">School</SelectItem>
                      <SelectItem value="church">Church</SelectItem>
                      <SelectItem value="sunday_school">Sunday School</SelectItem>
                      <SelectItem value="homeschool_coop">Homeschool Co-op</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Student Licenses</Label>
                  <Input
                    type="number"
                    value={newOrg.student_licenses}
                    onChange={(e) => setNewOrg({ ...newOrg, student_licenses: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    value={newOrg.contact_email}
                    onChange={(e) => setNewOrg({ ...newOrg, contact_email: e.target.value })}
                    className="mt-1"
                    placeholder="contact@school.edu"
                    data-testid="create-org-contact-email"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    type="tel"
                    value={newOrg.contact_phone}
                    onChange={(e) => setNewOrg({ ...newOrg, contact_phone: e.target.value })}
                    className="mt-1"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Admin User Details */}
            <div className="border-b pb-4">
              <h4 className="font-medium text-slate-700 mb-3">Organization Admin Account</h4>
              <p className="text-sm text-slate-500 mb-3">This user will manage the organization.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Admin Name *</Label>
                  <Input
                    value={newOrg.admin_name}
                    onChange={(e) => setNewOrg({ ...newOrg, admin_name: e.target.value })}
                    className="mt-1"
                    placeholder="John Smith"
                    data-testid="create-org-admin-name"
                  />
                </div>
                <div>
                  <Label>Admin Email *</Label>
                  <Input
                    type="email"
                    value={newOrg.admin_email}
                    onChange={(e) => setNewOrg({ ...newOrg, admin_email: e.target.value })}
                    className="mt-1"
                    placeholder="admin@school.edu"
                    data-testid="create-org-admin-email"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Admin Password *</Label>
                  <Input
                    type="password"
                    value={newOrg.admin_password}
                    onChange={(e) => setNewOrg({ ...newOrg, admin_password: e.target.value })}
                    className="mt-1"
                    placeholder="Min. 6 characters"
                    data-testid="create-org-admin-password"
                  />
                </div>
              </div>
            </div>

            {/* Integrations */}
            <div>
              <h4 className="font-medium text-slate-700 mb-3">Integrations</h4>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Google Classroom</p>
                    <p className="text-sm text-slate-500">Enable classroom integration for this organization</p>
                  </div>
                </div>
                <Switch
                  checked={newOrg.google_classroom_enabled}
                  onCheckedChange={(checked) => setNewOrg({ ...newOrg, google_classroom_enabled: checked })}
                  data-testid="create-org-google-classroom"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOrgDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateOrganization} disabled={creatingOrg} data-testid="create-org-submit">
              {creatingOrg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Organization Dialog */}
      <Dialog open={archiveOrgDialog.open} onOpenChange={(open) => setArchiveOrgDialog({ ...archiveOrgDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{archiveOrgDialog.isArchived ? 'Restore' : 'Archive'} Organization</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to {archiveOrgDialog.isArchived ? 'restore' : 'archive'} <strong>{archiveOrgDialog.orgName}</strong>?</p>
            {!archiveOrgDialog.isArchived && (
              <p className="text-sm text-slate-500 mt-2">Archived organizations will be hidden but can be restored later.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOrgDialog({ open: false, orgId: null, orgName: '', isArchived: false })}>
              Cancel
            </Button>
            <Button onClick={handleArchiveOrganization} disabled={archivingOrg}>
              {archivingOrg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {archiveOrgDialog.isArchived ? 'Restore' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserDialog.open} onOpenChange={(open) => setDeleteUserDialog({ ...deleteUserDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete <strong>{deleteUserDialog.userName}</strong>?</p>
            <p className="text-sm text-red-600 mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserDialog({ open: false, userId: null, userName: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Promo Code to User Dialog */}
      <Dialog open={applyPromoDialog} onOpenChange={setApplyPromoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-emerald-600" />
              Apply Promo Code
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Applying promo code to:</p>
              <p className="font-medium text-slate-800">{applyPromoUserEmail}</p>
            </div>
            <div>
              <Label>Promo Code</Label>
              <Input
                value={applyPromoCode}
                onChange={(e) => setApplyPromoCode(e.target.value.toUpperCase())}
                placeholder="e.g., WELCOME25"
                className="uppercase mt-1"
                data-testid="admin-apply-promo-input"
              />
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> If the user has an active Stripe subscription, the discount will be applied to their subscription automatically.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyPromoDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleApplyPromoToUser} 
              disabled={!applyPromoCode.trim() || applyingPromo}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="admin-apply-promo-btn"
            >
              {applyingPromo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Tag className="w-4 h-4 mr-2" />}
              Apply Promo Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog.open} onOpenChange={(open) => setEditUserDialog({ ...editUserDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {editUserDialog.user && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editUserDialog.user.name || ''}
                    onChange={(e) => setEditUserDialog({
                      ...editUserDialog,
                      user: { ...editUserDialog.user, name: e.target.value }
                    })}
                    className="mt-1"
                    data-testid="edit-user-name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editUserDialog.user.email || ''}
                    onChange={(e) => setEditUserDialog({
                      ...editUserDialog,
                      user: { ...editUserDialog.user, email: e.target.value }
                    })}
                    className="mt-1"
                    data-testid="edit-user-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Account Type</Label>
                  <Select
                    value={editUserDialog.user.account_type || 'parent'}
                    onValueChange={(v) => setEditUserDialog({
                      ...editUserDialog,
                      user: { ...editUserDialog.user, account_type: v }
                    })}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-user-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="teacher">Teacher</SelectItem>
                      <SelectItem value="homeschool">Homeschool</SelectItem>
                      <SelectItem value="org_admin">Org Admin</SelectItem>
                      <SelectItem value="organization">Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subscription Plan</Label>
                  <Select
                    value={editUserDialog.user.subscription_plan || 'free'}
                    onValueChange={(v) => setEditUserDialog({
                      ...editUserDialog,
                      user: { ...editUserDialog.user, subscription_plan: v }
                    })}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-user-plan">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Admin Access</Label>
                  <p className="text-xs text-slate-500">Grant admin privileges to this user</p>
                </div>
                <Switch
                  checked={editUserDialog.user.is_admin || false}
                  onCheckedChange={(checked) => setEditUserDialog({
                    ...editUserDialog,
                    user: { ...editUserDialog.user, is_admin: checked }
                  })}
                  data-testid="edit-user-admin"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Christian Education Access</Label>
                  <p className="text-xs text-slate-500">Enable Christian Education module</p>
                </div>
                <Switch
                  checked={editUserDialog.user.christian_education_enabled || false}
                  onCheckedChange={(checked) => setEditUserDialog({
                    ...editUserDialog,
                    user: { ...editUserDialog.user, christian_education_enabled: checked }
                  })}
                  data-testid="edit-user-christian"
                />
              </div>
              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setResetPasswordDialog({
                      open: true,
                      userId: editUserDialog.user.id,
                      userName: editUserDialog.user.name
                    });
                  }}
                  data-testid="reset-password-btn"
                >
                  <Key className="w-4 h-4 mr-2" /> Reset Password
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog({ open: false, user: null })}>Cancel</Button>
            <Button
              onClick={() => handleUpdateUser(editUserDialog.user.id, {
                name: editUserDialog.user.name,
                email: editUserDialog.user.email,
                account_type: editUserDialog.user.account_type,
                subscription_plan: editUserDialog.user.subscription_plan,
                is_admin: editUserDialog.user.is_admin,
                christian_education_enabled: editUserDialog.user.christian_education_enabled
              })}
              disabled={savingUser}
              data-testid="save-user-btn"
            >
              {savingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => setResetPasswordDialog({ ...resetPasswordDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Set a new password for <strong>{resetPasswordDialog.userName}</strong>
            </p>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Create a strong password"
                className="mt-1"
                data-testid="new-password-input"
              />
              <div className="mt-2">
                <PasswordStrengthMeter password={newPassword} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setResetPasswordDialog({ open: false, userId: null, userName: '' });
              setNewPassword('');
            }}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={savingUser} data-testid="confirm-reset-btn">
              {savingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Employee Dialog */}
      <Dialog open={addEmployeeDialog} onOpenChange={setAddEmployeeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                  placeholder="John Doe"
                  className="mt-1"
                  data-testid="employee-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  placeholder="john@zazued.com"
                  className="mt-1"
                  data-testid="employee-email"
                />
              </div>
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={newEmployee.password}
                onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                placeholder="Initial password"
                className="mt-1"
                data-testid="employee-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role</Label>
                <Select
                  value={newEmployee.role}
                  onValueChange={(v) => setNewEmployee({ ...newEmployee, role: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="employee-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="support">Support Agent</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="content">Content Creator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department</Label>
                <Select
                  value={newEmployee.department}
                  onValueChange={(v) => setNewEmployee({ ...newEmployee, department: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="employee-department">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="support">Support</SelectItem>
                    <SelectItem value="engineering">Engineering</SelectItem>
                    <SelectItem value="content">Content</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="management">Management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEmployeeDialog(false)}>Cancel</Button>
            <Button onClick={handleAddEmployee} disabled={addingEmployee} data-testid="add-employee-submit">
              {addingEmployee && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Team Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editOrgDialog.open} onOpenChange={(open) => setEditOrgDialog({ ...editOrgDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
          </DialogHeader>
          {editOrgDialog.org && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Organization Name</Label>
                <Input
                  value={editOrgDialog.org.name || ''}
                  onChange={(e) => setEditOrgDialog({
                    ...editOrgDialog,
                    org: { ...editOrgDialog.org, name: e.target.value }
                  })}
                  className="mt-1"
                  data-testid="edit-org-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Organization Type</Label>
                  <Select
                    value={editOrgDialog.org.organization_type || 'school'}
                    onValueChange={(v) => setEditOrgDialog({
                      ...editOrgDialog,
                      org: { ...editOrgDialog.org, organization_type: v }
                    })}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-org-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="school">School</SelectItem>
                      <SelectItem value="church">Church</SelectItem>
                      <SelectItem value="sunday_school">Sunday School</SelectItem>
                      <SelectItem value="homeschool_coop">Homeschool Co-op</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Student Licenses</Label>
                  <Input
                    type="number"
                    value={editOrgDialog.org.student_licenses || 0}
                    onChange={(e) => setEditOrgDialog({
                      ...editOrgDialog,
                      org: { ...editOrgDialog.org, student_licenses: parseInt(e.target.value) || 0 }
                    })}
                    className="mt-1"
                    data-testid="edit-org-licenses"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={editOrgDialog.org.contact_email || ''}
                    onChange={(e) => setEditOrgDialog({
                      ...editOrgDialog,
                      org: { ...editOrgDialog.org, contact_email: e.target.value }
                    })}
                    className="mt-1"
                    data-testid="edit-org-email"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={editOrgDialog.org.contact_phone || ''}
                    onChange={(e) => setEditOrgDialog({
                      ...editOrgDialog,
                      org: { ...editOrgDialog.org, contact_phone: e.target.value }
                    })}
                    className="mt-1"
                    data-testid="edit-org-phone"
                  />
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Account Number: <span className="font-mono font-medium">{editOrgDialog.org.account_number || 'N/A'}</span></p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOrgDialog({ open: false, org: null })}>Cancel</Button>
            <Button onClick={handleUpdateOrganization} disabled={savingOrg} data-testid="save-org-btn">
              {savingOrg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkUpdateDialog} onOpenChange={setBulkUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Update Users</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Update {selectedUsers.length} selected user(s). Leave fields empty to skip.
            </p>
            <div>
              <Label>Subscription Plan</Label>
              <Select 
                value={bulkUpdateData.subscription_plan} 
                onValueChange={(v) => setBulkUpdateData({ ...bulkUpdateData, subscription_plan: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="No change" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No change</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account Type</Label>
              <Select 
                value={bulkUpdateData.account_type} 
                onValueChange={(v) => setBulkUpdateData({ ...bulkUpdateData, account_type: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="No change" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No change</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="org_admin">Organization Admin</SelectItem>
                  <SelectItem value="homeschool">Homeschool</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setBulkUpdateDialog(false);
              setBulkUpdateData({ subscription_plan: '', account_type: '' });
            }}>
              Cancel
            </Button>
            <Button onClick={handleBulkUpdate} disabled={bulkUpdating}>
              {bulkUpdating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update {selectedUsers.length} Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Details Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ticket Details</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Subject</Label>
                  <p className="font-medium">{selectedTicket.subject}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Select value={selectedTicket.status} onValueChange={(v) => handleUpdateTicket(selectedTicket.id, { status: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Description</Label>
                <p className="p-3 bg-slate-50 rounded-lg text-sm mt-1">{selectedTicket.description}</p>
              </div>
              {selectedTicket.admin_response && (
                <div>
                  <Label className="text-xs text-slate-500">Previous Response</Label>
                  <p className="p-3 bg-green-50 rounded-lg text-sm mt-1 border-l-4 border-green-500">{selectedTicket.admin_response}</p>
                </div>
              )}
              <div>
                <Label>Reply</Label>
                <Textarea
                  value={ticketResponse}
                  onChange={(e) => setTicketResponse(e.target.value)}
                  className="mt-1"
                  placeholder="Type your response..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTicket(null)}>Close</Button>
            <Button onClick={handleRespondToTicket} disabled={respondingTicket}>
              {respondingTicket && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Quote Dialog */}
      <Dialog open={createQuoteDialog} onOpenChange={setCreateQuoteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Organization Name *</Label>
                <Input value={newQuote.organization_name} onChange={(e) => setNewQuote({...newQuote, organization_name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input value={newQuote.contact_name} onChange={(e) => setNewQuote({...newQuote, contact_name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Contact Email *</Label>
                <Input type="email" value={newQuote.contact_email} onChange={(e) => setNewQuote({...newQuote, contact_email: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={newQuote.phone} onChange={(e) => setNewQuote({...newQuote, phone: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Number of Students *</Label>
                <Input type="number" value={newQuote.student_count} onChange={(e) => setNewQuote({...newQuote, student_count: parseInt(e.target.value) || 0})} className="mt-1" />
              </div>
              <div>
                <Label>Plan</Label>
                <Select value={newQuote.plan} onValueChange={(v) => setNewQuote({...newQuote, plan: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic ($9.99/student/mo)</SelectItem>
                    <SelectItem value="premium">Premium ($19.99/student/mo)</SelectItem>
                    <SelectItem value="enterprise">Enterprise ($49.99/student/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Billing Cycle</Label>
                <Select value={newQuote.billing_cycle} onValueChange={(v) => setNewQuote({...newQuote, billing_cycle: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual (15% discount)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Additional Discount %</Label>
                <Input type="number" min="0" max="30" value={newQuote.discount_percent} onChange={(e) => setNewQuote({...newQuote, discount_percent: parseFloat(e.target.value) || 0})} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Input value={newQuote.notes} onChange={(e) => setNewQuote({...newQuote, notes: e.target.value})} className="mt-1" placeholder="Special terms or notes..." />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateQuoteDialog(false)}>Cancel</Button>
            <Button onClick={generateQuote} className="bg-emerald-500 hover:bg-emerald-600">Generate Quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Purchase Order Dialog */}
      <Dialog open={createPODialog} onOpenChange={setCreatePODialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Organization Name *</Label>
                <Input value={newPO.organization_name} onChange={(e) => setNewPO({...newPO, organization_name: e.target.value})} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label>Billing Address *</Label>
                <Input value={newPO.billing_address} onChange={(e) => setNewPO({...newPO, billing_address: e.target.value})} className="mt-1" placeholder="123 Main St, City, State, ZIP" />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input value={newPO.contact_name} onChange={(e) => setNewPO({...newPO, contact_name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Contact Email *</Label>
                <Input type="email" value={newPO.contact_email} onChange={(e) => setNewPO({...newPO, contact_email: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={newPO.phone} onChange={(e) => setNewPO({...newPO, phone: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Number of Students *</Label>
                <Input type="number" value={newPO.student_count} onChange={(e) => setNewPO({...newPO, student_count: parseInt(e.target.value) || 0})} className="mt-1" />
              </div>
              <div>
                <Label>Plan</Label>
                <Select value={newPO.plan} onValueChange={(v) => setNewPO({...newPO, plan: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={newPO.payment_terms} onValueChange={(v) => setNewPO({...newPO, payment_terms: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NET30">NET 30</SelectItem>
                    <SelectItem value="NET60">NET 60</SelectItem>
                    <SelectItem value="NET90">NET 90</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Input value={newPO.notes} onChange={(e) => setNewPO({...newPO, notes: e.target.value})} className="mt-1" placeholder="PO reference, special instructions..." />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePODialog(false)}>Cancel</Button>
            <Button onClick={generatePurchaseOrder} className="bg-purple-500 hover:bg-purple-600">Create PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Purchase Order Dialog */}
      <Dialog open={editPODialog} onOpenChange={setEditPODialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Purchase Order</DialogTitle>
          </DialogHeader>
          {editPOData && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Organization Name</Label>
                  <Input value={editPOData.organization_name || ''} onChange={(e) => setEditPOData({...editPOData, organization_name: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label>Contact Name</Label>
                  <Input value={editPOData.contact_name || ''} onChange={(e) => setEditPOData({...editPOData, contact_name: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input value={editPOData.contact_email || ''} onChange={(e) => setEditPOData({...editPOData, contact_email: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label>Student Count</Label>
                  <Input type="number" value={editPOData.student_count || 0} onChange={(e) => setEditPOData({...editPOData, student_count: parseInt(e.target.value) || 0})} className="mt-1" />
                </div>
                <div>
                  <Label>Plan</Label>
                  <Select value={editPOData.plan || 'enterprise'} onValueChange={(v) => setEditPOData({...editPOData, plan: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Billing Cycle</Label>
                  <Select value={editPOData.billing_cycle || 'annual'} onValueChange={(v) => setEditPOData({...editPOData, billing_cycle: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Terms</Label>
                  <Select value={editPOData.payment_terms || 'NET30'} onValueChange={(v) => setEditPOData({...editPOData, payment_terms: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NET30">NET 30</SelectItem>
                      <SelectItem value="NET60">NET 60</SelectItem>
                      <SelectItem value="NET90">NET 90</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editPOData.status || 'pending'} onValueChange={(v) => setEditPOData({...editPOData, status: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Billing Address</Label>
                  <Input value={editPOData.billing_address || ''} onChange={(e) => setEditPOData({...editPOData, billing_address: e.target.value})} className="mt-1" />
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Input value={editPOData.notes || ''} onChange={(e) => setEditPOData({...editPOData, notes: e.target.value})} className="mt-1" />
                </div>
                <div className="col-span-2 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <strong>Current Total:</strong> ${editPOData.total?.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Note: Total will be recalculated if you change student count, plan, or billing cycle.
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditPODialog(false); setEditPOData(null); }}>Cancel</Button>
            <Button onClick={handleEditPO} className="bg-emerald-500 hover:bg-emerald-600">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Scheduled Report Dialog */}
      <Dialog open={createReportDialog} onOpenChange={setCreateReportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Scheduled Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Report Name *</Label>
              <Input
                value={newReport.name}
                onChange={(e) => setNewReport({ ...newReport, name: e.target.value })}
                className="mt-1"
                placeholder="Weekly Usage Summary"
                data-testid="report-name-input"
              />
            </div>
            <div>
              <Label>Report Type</Label>
              <Select
                value={newReport.report_type}
                onValueChange={(v) => setNewReport({ ...newReport, report_type: v })}
              >
                <SelectTrigger className="mt-1" data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly_usage">Weekly Usage Summary</SelectItem>
                  <SelectItem value="monthly_revenue">Monthly Revenue Report</SelectItem>
                  <SelectItem value="user_growth">User Growth Report</SelectItem>
                  <SelectItem value="engagement">Engagement Metrics</SelectItem>
                  <SelectItem value="organization_summary">Organization Summary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frequency</Label>
              <Select
                value={newReport.frequency}
                onValueChange={(v) => setNewReport({ ...newReport, frequency: v })}
              >
                <SelectTrigger className="mt-1" data-testid="report-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newReport.frequency === 'weekly' && (
              <div>
                <Label>Day of Week</Label>
                <Select
                  value={String(newReport.day_of_week)}
                  onValueChange={(v) => setNewReport({ ...newReport, day_of_week: parseInt(v) })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Monday</SelectItem>
                    <SelectItem value="1">Tuesday</SelectItem>
                    <SelectItem value="2">Wednesday</SelectItem>
                    <SelectItem value="3">Thursday</SelectItem>
                    <SelectItem value="4">Friday</SelectItem>
                    <SelectItem value="5">Saturday</SelectItem>
                    <SelectItem value="6">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Send Time (Hour)</Label>
              <Select
                value={String(newReport.hour)}
                onValueChange={(v) => setNewReport({ ...newReport, hour: parseInt(v) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(24)].map((_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {i.toString().padStart(2, '0')}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Recipients (comma-separated emails) *</Label>
              <Textarea
                value={newReport.recipients}
                onChange={(e) => setNewReport({ ...newReport, recipients: e.target.value })}
                className="mt-1"
                placeholder="admin@example.com, manager@example.com"
                data-testid="report-recipients-input"
              />
            </div>
            <div>
              <Label>Organization (optional - leave blank for all)</Label>
              <Select
                value={newReport.filters?.organization_id || ''}
                onValueChange={(v) => setNewReport({ 
                  ...newReport, 
                  filters: { ...newReport.filters, organization_id: v === 'all' ? '' : v }
                })}
              >
                <SelectTrigger className="mt-1" data-testid="report-org-select">
                  <SelectValue placeholder="All Organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.account_number ? `${org.account_number} - ` : ''}{org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateReportDialog(false)}>Cancel</Button>
            <Button onClick={createScheduledReport} data-testid="create-report-submit">
              Create Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tier Features Dialog */}
      <Dialog open={editTierDialog} onOpenChange={setEditTierDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {editingTier?.tier_id?.charAt(0).toUpperCase() + editingTier?.tier_id?.slice(1)} Tier Features</DialogTitle>
          </DialogHeader>
          {editingTier && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Max Children (-1 for unlimited)</Label>
                <Input
                  type="number"
                  value={editingTier.max_children}
                  onChange={(e) => setEditingTier({ ...editingTier, max_children: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="tier-max-children-input"
                />
              </div>
              <div>
                <Label>Daily Activity Limit (-1 for unlimited)</Label>
                <Input
                  type="number"
                  value={editingTier.daily_activity_limit}
                  onChange={(e) => setEditingTier({ ...editingTier, daily_activity_limit: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="tier-daily-limit-input"
                />
              </div>
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <Label>Progress Reports</Label>
                  <Switch
                    checked={editingTier.progress_reports}
                    onCheckedChange={(checked) => setEditingTier({ ...editingTier, progress_reports: checked })}
                    data-testid="tier-progress-reports-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Bible Curriculum</Label>
                  <Switch
                    checked={editingTier.bible_curriculum}
                    onCheckedChange={(checked) => setEditingTier({ ...editingTier, bible_curriculum: checked })}
                    data-testid="tier-bible-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Priority Support</Label>
                  <Switch
                    checked={editingTier.priority_support}
                    onCheckedChange={(checked) => setEditingTier({ ...editingTier, priority_support: checked })}
                    data-testid="tier-priority-support-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Adaptive Learning</Label>
                  <Switch
                    checked={editingTier.adaptive_learning}
                    onCheckedChange={(checked) => setEditingTier({ ...editingTier, adaptive_learning: checked })}
                    data-testid="tier-adaptive-learning-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Custom Curriculum</Label>
                  <Switch
                    checked={editingTier.custom_curriculum}
                    onCheckedChange={(checked) => setEditingTier({ ...editingTier, custom_curriculum: checked })}
                    data-testid="tier-custom-curriculum-toggle"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTierDialog(false)}>Cancel</Button>
            <Button onClick={updateTierFeatures} data-testid="update-tier-submit">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process License Request Dialog */}
      <Dialog open={processRequestDialog.open} onOpenChange={(open) => setProcessRequestDialog({ ...processRequestDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process License Request</DialogTitle>
          </DialogHeader>
          {processRequestDialog.request && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <p><strong>Organization:</strong> {processRequestDialog.request.organization_name}</p>
                <p><strong>Requested:</strong> {processRequestDialog.request.requested_licenses} licenses</p>
                <p><strong>Current:</strong> {processRequestDialog.request.current_licenses} licenses</p>
                <p><strong>Reason:</strong> {processRequestDialog.request.reason || 'Not provided'}</p>
              </div>
              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="mt-1"
                  placeholder="Add any notes about this decision..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessRequestDialog({ open: false, request: null })}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => handleProcessLicenseRequest('reject')}
              disabled={processingRequest}
            >
              {processingRequest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reject
            </Button>
            <Button
              onClick={() => handleProcessLicenseRequest('approve')}
              disabled={processingRequest}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {processingRequest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Pricing Plan Dialog */}
      <Dialog open={createPlanDialog} onOpenChange={setCreatePlanDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Pricing Plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan ID *</Label>
                <Input
                  value={newPlan.plan_id}
                  onChange={(e) => setNewPlan({ ...newPlan, plan_id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="mt-1"
                  placeholder="e.g., basic, premium"
                  data-testid="new-plan-id"
                />
              </div>
              <div>
                <Label>Plan Type</Label>
                <Select
                  value={newPlan.plan_type}
                  onValueChange={(v) => setNewPlan({ ...newPlan, plan_type: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="new-plan-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="family">Family</SelectItem>
                    <SelectItem value="organization">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Plan Name *</Label>
              <Input
                value={newPlan.name}
                onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                className="mt-1"
                placeholder="e.g., Basic Plan, Premium Plan"
                data-testid="new-plan-name"
              />
            </div>
            <div>
              <Label>Monthly Price ($) *</Label>
              <Input
                type="number"
                step="0.01"
                value={newPlan.price}
                onChange={(e) => setNewPlan({ ...newPlan, price: parseFloat(e.target.value) || 0 })}
                className="mt-1"
                placeholder="9.99"
                data-testid="new-plan-price"
              />
            </div>
            <div>
              <Label>Features (comma-separated) *</Label>
              <Textarea
                value={newPlan.features}
                onChange={(e) => setNewPlan({ ...newPlan, features: e.target.value })}
                className="mt-1"
                placeholder="5 children, All subjects, Basic analytics"
                data-testid="new-plan-features"
                rows={3}
              />
              <p className="text-xs text-slate-500 mt-1">Enter features separated by commas</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePlanDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePlan} disabled={savingPlan} data-testid="create-plan-submit">
              {savingPlan && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Pricing Plan Dialog */}
      <Dialog open={editPlanDialog} onOpenChange={setEditPlanDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Pricing Plan</DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Plan ID</p>
                  <p className="font-mono font-medium">{editingPlan.plan_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Active</Label>
                  <Switch
                    checked={editingPlan.is_active !== false}
                    onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, is_active: checked })}
                    data-testid="edit-plan-active"
                  />
                </div>
              </div>
              <div>
                <Label>Plan Name *</Label>
                <Input
                  value={editingPlan.name}
                  onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                  className="mt-1"
                  data-testid="edit-plan-name"
                />
              </div>
              <div>
                <Label>Monthly Price ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingPlan.price}
                  onChange={(e) => setEditingPlan({ ...editingPlan, price: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="edit-plan-price"
                />
              </div>
              <div>
                <Label>Features (comma-separated)</Label>
                <Textarea
                  value={editingPlan.features}
                  onChange={(e) => setEditingPlan({ ...editingPlan, features: e.target.value })}
                  className="mt-1"
                  data-testid="edit-plan-features"
                  rows={4}
                />
                <p className="text-xs text-slate-500 mt-1">Enter features separated by commas</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPlanDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdatePlan} disabled={savingPlan} data-testid="edit-plan-submit">
              {savingPlan && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdminDashboard;
